import { fmtTs } from '../utils/time';
import React, { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { api, SERVER_ORIGIN } from '../services/api';
import {
  Plus, Search, ChevronDown, X, Phone, Mail,
  MapPin, DollarSign, Clock, Calendar, Edit2, Trash2, Archive, ArchiveRestore,
  Upload, Crosshair, CheckCircle, AlertCircle, Briefcase, FileText, ClipboardList, HardHat,
  ExternalLink, StickyNote, Gift, Map as MapIcon, MessageSquare
} from 'lucide-react';
import { toast } from 'react-toastify';
import AddressAutocomplete from '../components/AddressAutocomplete';
import StartPlanForm from '../components/StartPlanForm';
import CustomPlanBuilder from '../components/CustomPlanBuilder';
import PlanBidButton from '../components/PlanBidButton';
import CustomerView from '../components/CustomerView';
import CustomerIntake, { WhatsNext, TurfNote } from '../components/CustomerIntake';
import ReminderAdd from '../components/ReminderAdd';
import TodoSheet from '../components/TodoSheet';
import LawnWalkthroughCard from '../components/LawnWalkthroughCard';
import PaymentAutopayBlock from '../components/PaymentAutopayBlock';
import SignedAndPaidBlock from '../components/SignedAndPaidBlock';
import { useReadOnly } from '../hooks/useReadOnly';
import AccountMark from '../components/AccountMark';

// ── Types ─────────────────────────────────────────────────────────────────────
interface Customer {
  reviewed_at?: string | null; reviewed_note?: string | null;
  id: number; name: string; display_name?: string; email?: string; phone?: string;
  how_heard?: string; notes?: string; is_archived?: number;
  sms_opt_in?: number; marketing_opt_in?: number; transactional_messages_enabled?: number;
  title?: string; company_name?: string; source?: string;
  first_name?: string | null; last_name?: string | null;
  property_count?: number; cancelled_property_count?: number;
  service_mode?: string; service_resume_on?: string | null;
  last_service_date?: string | null; next_visit_date?: string | null; mowing_total?: number | null;
  addresses?: string | null; city?: string | null;
  account_mark?: 'P' | 'C' | null;   // the same person also has the other kind of account (server/services/twins)
}
interface Property {
  id: number; customer_id: number; label?: string; address: string;
  city?: string; state?: string; zip_code?: string;
  gate_notes?: string; access_notes?: string; notes?: string;
  mowing_price?: number; estimated_minutes?: number; lot_sqft?: number | null; treatable_sqft?: number | null;
  turf_source?: string | null; turf_flag?: string | null; turf_band?: number | null; turf_assessed_at?: string | null;
  cutting_height?: string; crew_notes?: string; lat?: number; lng?: number;
  fuel_level?: string | null;
  travel_surcharge?: number | null; travel_miles_out?: number | null;
  service_status?: string; service_cancelled_effective?: string | null; service_cancel_reason?: string | null;
}

const fmtShortDate = (iso: string) => new Date(String(iso).slice(0, 10) + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

const HOW_HEARD = ['Referral', 'Nextdoor', 'Google', 'Facebook', 'Flyer', 'Walk-by', 'Other'];
const CUTTING_HEIGHTS = ['2"', '2.5"', '3"', '3.5"', '4"'];
const TITLES = ['', 'Mr.', 'Mrs.', 'Ms.'];

const emptyCust = {
  title: '', name: '', first_name: '', last_name: '', company_name: '', email: '', phone: '', how_heard: '', notes: '',
  transactional_messages_enabled: true, sms_opt_in: false, marketing_opt_in: false,
  source: 'direct',
};
const emptyProp = {
  label: '', address: '', city: 'Austin', state: 'TX', zip_code: '',
  gate_notes: '', access_notes: '', notes: '',
  mowing_price: '', estimated_minutes: '', cutting_height: '', crew_notes: '', fuel_level: '', lot_sqft: '', treatable_sqft: '',
  travel_surcharge: '' as string | number, travel_miles_out: null as number | null,
  lat: null as number | null, lng: null as number | null,
};
// No placeholder label: "Main property" used to reach the crew as a stop's
// title. Left blank, every screen falls back to the address.
const emptyPropForCust = { ...emptyProp };

// ── CSV column spec ───────────────────────────────────────────────────────────
const CSV_COLUMNS = [
  'customer_name','phone','email','address','city','zip_code',
  'property_label','mowing_price','estimated_minutes','cutting_height',
  'notes','crew_job_notes','sms_opt_in','marketing_opt_in',
];

function parseCSV(text: string): Record<string, string>[] {
  const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
  if (lines.length < 2) return [];
  const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, ''));
  return lines.slice(1).map(line => {
    const vals: string[] = [];
    let current = ''; let inQuote = false;
    for (const ch of line) {
      if (ch === '"') { inQuote = !inQuote; }
      else if (ch === ',' && !inQuote) { vals.push(current.trim()); current = ''; }
      else { current += ch; }
    }
    vals.push(current.trim());
    const row: Record<string, string> = {};
    headers.forEach((h, i) => { if (h) row[h] = vals[i] || ''; });
    return row;
  }).filter(r => Object.values(r).some(v => v));
}

export default function Customers() {
  const readOnly = useReadOnly();   // view-only login: grey out what can only ever be refused
  const qc = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<'name' | 'last' | 'first' | 'recent' | 'next' | 'newest' | 'price' | 'city'>('newest');

  const [custModal, setCustModal] = useState<'new' | 'edit' | null>(null);
  // What the intake starts with: a lead being converted, or a name from My Day.
  const [intakePrefill, setIntakePrefill] = useState<any>(null);
  const [justMade, setJustMade] = useState<{ customer: any; property?: any; area?: any; turf?: any } | null>(null);
  // Arriving from Leads "Convert" (state), My Day "Customer" (?new=1) or a duplicate check (?search=).
  useEffect(() => {
    const prefill = (location.state as any)?.prefillLead;
    const params = new URLSearchParams(location.search);
    if (prefill) {
      setIntakePrefill({
        lead_id: prefill.lead_id, name: prefill.name || '', phone: prefill.phone || '', email: prefill.email || '',
        address: prefill.address || '', city: prefill.city || '', how_heard: prefill.how_heard || '', wants: prefill.wants || '',
        notes: prefill.notes || '', source: prefill.source || 'lead',
      });
      setCustModal('new');
      // Clear state so a back-nav doesn't re-open the modal
      window.history.replaceState({}, '');
    } else if (params.get('new') === '1') {
      setCustModal('new');
    }
    if (params.get('search')) setSearch(params.get('search') || '');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [custForm, setCustForm] = useState<any>(emptyCust);
  const [editingCust, setEditingCust] = useState<Customer | null>(null);
  const [propModal, setPropModal] = useState<{ customerId: number; prop?: Property } | null>(null);
  const [propForm, setPropForm] = useState<any>(emptyProp);
  const [activeTab, setActiveTab] = useState<'active' | 'archived'>('active');
  const [csvModal, setCsvModal] = useState(false);
  const [profileCust, setProfileCust] = useState<Customer | null>(null);
  // Embedded property for new customer
  const [newCustProp, setNewCustProp] = useState<any>(emptyPropForCust);

  // ── AI intake: paste a text thread / voicemail / note → the form fills itself ──
  // The stand-in for the bidder flow until that side is finished. Nothing is
  // saved until "Create" — the draft just lands in the fields for a look-over.
  const { data: custData, isLoading } = useQuery(['customers', search, activeTab, sort], () => {
    const params: any = { sort };
    if (search) params.search = search;
    if (activeTab === 'archived') params.archived = '1';
    return api.get('/customers', { params }).then(r => r.data.customers as Customer[]);
  });

  // ?profile=ID (from My Day / Needs Attention) opens that customer once the list is here.
  useEffect(() => {
    const id = Number(new URLSearchParams(location.search).get('profile'));
    if (!id || !custData) return;
    // Asked to open a card while the New Customer form is up ("Steve already has that number — Open Steve"): the form
    // closes first. It used to stay on top, so the card opened behind it and looked like nothing happened
    // (owner, Sep 19 2026: "open customer still don't work").
    if (custModal === 'new') { setCustModal(null); setIntakePrefill(null); }
    // The ask is used up once the card is open: the address goes back to plain /customers, so closing the card — or the
    // list refreshing in the background — never opens it again by itself.
    const show = (c: Customer) => { setProfileCust(c); navigate('/customers', { replace: true }); };
    const c = custData.find(x => x.id === id);
    if (c) { show(c); return; }
    // Not in the list on screen (archived, or a search is on): ask for that one customer rather than opening nothing.
    api.get(`/customers/${id}`).then(r => { if (r.data?.customer) show(r.data.customer as Customer); })
      .catch(() => { toast.error("That customer isn't in the app any more"); navigate('/customers', { replace: true }); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [custData, location.search]);

  const { data: propData } = useQuery(['all-properties'], () =>
    api.get('/properties').then(r => r.data.properties as (Property & { customer_name: string })[])
  );

  // ── Customer mutations ────────────────────────────────────────────────────────
  const saveCust = useMutation(async () => {
    const fullName = [custForm.first_name, custForm.last_name].map((x: string) => String(x || '').trim()).filter(Boolean).join(' ');
    if (!fullName) throw new Error('First name is required');
    if (editingCust) {
      await api.put(`/customers/${editingCust.id}`, { ...custForm, name: fullName });
      return null;
    } else {
      const payload: any = { ...custForm, name: fullName };
      if (newCustProp.address.trim()) {
        payload.property = {
          label: newCustProp.label?.trim() || null,
          address: newCustProp.address,
          city: newCustProp.city || null,
          state: newCustProp.state || 'TX',
          zip_code: newCustProp.zip_code || null,
          lat: newCustProp.lat || null,
          lng: newCustProp.lng || null,
          mowing_price: newCustProp.mowing_price ? Number(newCustProp.mowing_price) : null,
          estimated_minutes: newCustProp.estimated_minutes ? Number(newCustProp.estimated_minutes) : null,
          cutting_height: newCustProp.cutting_height || null,
          crew_notes: newCustProp.crew_notes || null,
          gate_notes: newCustProp.gate_notes || null,
          notes: newCustProp.notes || null,
        };
      }
      const res = await api.post('/customers', payload);
      return res.data.customer as Customer;
    }
  }, {
    onSuccess: (newCust) => {
      qc.invalidateQueries('customers');
      qc.invalidateQueries('all-properties');
      if (newCust) {
        toast.success('Customer created');
        setCustModal(null); setCustForm(emptyCust);
        setNewCustProp(emptyPropForCust);

      } else {
        toast.success('Customer updated');
        setCustModal(null); setEditingCust(null); setCustForm(emptyCust);
      }
    },
    onError: (e: any) => { toast.error(e.message || 'Failed to save'); },
  });

  const deleteCust = useMutation(async (id: number) => { await api.delete(`/customers/${id}`); }, {
    onSuccess: () => { qc.invalidateQueries('customers'); qc.invalidateQueries('all-properties'); toast.success('Customer permanently deleted'); },
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Failed to delete'); },
  });

  const archiveCust = useMutation(async (id: number) => { await api.post(`/customers/${id}/archive`); }, {
    onSuccess: () => { qc.invalidateQueries('customers'); toast.success('Customer archived'); },
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Failed to archive'); },
  });

  const restoreCust = useMutation(async (id: number) => { await api.post(`/customers/${id}/restore`); }, {
    onSuccess: () => { qc.invalidateQueries('customers'); toast.success('Customer restored'); },
    onError: () => { toast.error('Failed to restore'); },
  });

  // ── Property mutations ────────────────────────────────────────────────────────
  const saveProp = useMutation(async () => {
    if (!propForm.address.trim()) throw new Error('Address is required');
    const payload = {
      ...propForm,
      customer_id: propModal!.customerId,
      mowing_price: propForm.mowing_price ? Number(propForm.mowing_price) : null,
      estimated_minutes: propForm.estimated_minutes ? Number(propForm.estimated_minutes) : null,
      fuel_level: propForm.fuel_level || null,
      lot_sqft: propForm.lot_sqft === '' ? '' : Number(propForm.lot_sqft) || '',
      treatable_sqft: propForm.treatable_sqft === '' ? null : Number(propForm.treatable_sqft) || null,
    };
    if (propModal!.prop) {
      await api.put(`/properties/${propModal!.prop.id}`, payload);
    } else {
      await api.post('/properties', payload);
    }
  }, {
    onSuccess: () => {
      qc.invalidateQueries('all-properties');
      toast.success(propModal?.prop ? 'Property updated' : 'Property added');
      setPropModal(null); setPropForm(emptyProp);
    },
    onError: (e: any) => { toast.error(e.message || 'Failed to save property'); },
  });

  const deleteProp = useMutation(async (id: number) => { await api.delete(`/properties/${id}`); }, {
    onSuccess: () => { qc.invalidateQueries('all-properties'); toast.success('Property removed'); },
    onError: () => { toast.error('Failed to delete property'); },
  });

  // ── Helpers ───────────────────────────────────────────────────────────────────
  function openNewCust() {
    setEditingCust(null); setCustForm(emptyCust); setJustMade(null); setIntakePrefill(null);
    setNewCustProp(emptyPropForCust);
    setCustModal('new');
  }
  function openEditCust(c: Customer) {
    setEditingCust(c);
    const guess = String(c.name || '').trim().split(/\s+/);
    setCustForm({
      title: c.title || '', name: c.name,
      first_name: c.first_name ?? (guess.length > 1 ? guess.slice(0, -1).join(' ') : guess[0] || ''),
      last_name: c.last_name ?? (guess.length > 1 ? guess[guess.length - 1] : ''),
      company_name: c.company_name || '',
      email: c.email || '', phone: c.phone || '',
      how_heard: c.how_heard || '', notes: c.notes || '',
      transactional_messages_enabled: c.transactional_messages_enabled !== 0,
      sms_opt_in: !!c.sms_opt_in, marketing_opt_in: !!c.marketing_opt_in,
      source: c.source || 'direct',
    });
    setCustModal('edit');
  }
  function openNewProp(customerId: number) { setPropModal({ customerId }); setPropForm(emptyProp); }
  function openEditProp(customerId: number, prop: Property) {
    setPropModal({ customerId, prop });
    setPropForm({
      label: prop.label || '', address: prop.address, city: prop.city || 'Austin',
      state: prop.state || 'TX', zip_code: prop.zip_code || '',
      gate_notes: prop.gate_notes || '', access_notes: prop.access_notes || '',
      notes: prop.notes || '', mowing_price: prop.mowing_price ?? '',
      estimated_minutes: prop.estimated_minutes ?? '',
      cutting_height: prop.cutting_height || '', crew_notes: prop.crew_notes || '', fuel_level: prop.fuel_level || '',
      lot_sqft: prop.lot_sqft ?? '',
      treatable_sqft: prop.treatable_sqft ?? '',
      travel_surcharge: prop.travel_surcharge ?? '', travel_miles_out: prop.travel_miles_out ?? null,
      lat: prop.lat ?? null, lng: prop.lng ?? null,
    });
  }

  const customers = custData || [];
  const allProps = propData || [];
  const propsFor = (custId: number) => allProps.filter(p => p.customer_id === custId);
  const cf = (k: string) => (e: React.ChangeEvent<any>) => setCustForm((f: any) => ({ ...f, [k]: e.target.value }));

  return (
    <div className="space-y-5">
      {/* On a phone the buttons drop under the title instead of running off the edge. */}
      <div className="flex flex-wrap justify-between items-center gap-2">
        <h1 className="text-2xl sm:text-3xl font-black uppercase tracking-tight text-gray-900">Customers</h1>
        {activeTab === 'active' && (
          <div className="flex gap-2">
            <Link to="/customers/texts" className="btn-secondary">
              <MessageSquare className="h-4 w-4 mr-1" /> Who gets which texts
            </Link>
            <button onClick={() => setCsvModal(true)} className="btn-secondary">
              <Upload className="h-4 w-4 mr-1" /> Import CSV
            </button>
            <button onClick={openNewCust} className="btn-primary">
              <Plus className="h-4 w-4 mr-1" /> New Customer
            </button>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex rounded-xl bg-gray-200 p-1 w-fit">
        <button onClick={() => setActiveTab('active')}
          className={`px-4 py-2 text-sm font-semibold rounded-lg transition-colors ${activeTab === 'active' ? 'bg-white text-brand-700 shadow-sm' : 'text-gray-600'}`}>
          Active
        </button>
        <button onClick={() => setActiveTab('archived')}
          className={`px-4 py-2 text-sm font-semibold rounded-lg transition-colors flex items-center gap-1.5 ${activeTab === 'archived' ? 'bg-white text-brand-700 shadow-sm' : 'text-gray-600'}`}>
          <Archive className="h-3.5 w-3.5" /> Archived
        </button>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search anything — name, address, phone, email, zip, notes…" className="input pl-10" />
      </div>
      <div className="flex items-center gap-2 text-sm">
        <span className="text-gray-500">Sort</span>
        <select value={sort} onChange={e => setSort(e.target.value as any)} className="input w-auto py-1.5 text-sm">
          <option value="name">Name A–Z</option>
          <option value="last">Last name A–Z</option>
          <option value="first">First name A–Z</option>
          <option value="recent">Last service (newest first)</option>
          <option value="next">Next visit (soonest first)</option>
          <option value="newest">Newest customers</option>
          <option value="price">Mowing price (high to low)</option>
          <option value="city">City</option>
        </select>
        {search && <span className="text-xs text-gray-500">{(custData || []).length} match{(custData || []).length === 1 ? '' : 'es'}</span>}
      </div>

      {/* Customer list */}
      {isLoading ? (
        <div className="text-center text-gray-500 py-8">Loading…</div>
      ) : customers.length === 0 ? (
        <div className="card p-8 text-center text-gray-500">
          {search ? 'No customers match your search.'
            : activeTab === 'archived' ? 'No archived customers.'
            : 'No customers yet. Add your first one!'}
        </div>
      ) : (
        <div className="space-y-3">
          {customers.map(c => {
            const props = propsFor(c.id);
            return (
              <div key={c.id} className="card overflow-hidden">
                <div className="flex items-center px-4 py-3 hover:bg-gray-50 cursor-pointer"
                  onClick={() => setProfileCust(c)}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-gray-900">
                        {c.display_name || c.name}
                        <AccountMark mark={c.account_mark} />
                      </span>
                      {c.company_name && c.name && c.company_name !== c.name && (
                        <span className="text-xs text-gray-500">· {c.name}</span>
                      )}
                      {activeTab === 'active' && (
                        <span className="text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
                          {props.length} {props.length === 1 ? 'property' : 'properties'}
                        </span>
                      )}
                      {activeTab === 'archived' && (
                        <span className="text-xs text-orange-600 bg-orange-50 px-1.5 py-0.5 rounded">archived</span>
                      )}
                      {activeTab === 'active' && (c.cancelled_property_count || 0) > 0 && (
                        <span className="text-xs text-red-700 bg-red-50 border border-red-200 px-1.5 py-0.5 rounded font-medium">
                          {c.cancelled_property_count === c.property_count ? 'service cancelled' : `${c.cancelled_property_count} of ${c.property_count} cancelled`}
                        </span>
                      )}
                      {activeTab === 'active' && c.service_mode === 'as_needed' && (
                        <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded font-medium">as needed</span>
                      )}
                      {activeTab === 'active' && c.service_mode === 'off' && (
                        <span className="text-xs text-gray-700 bg-gray-100 border border-gray-200 px-1.5 py-0.5 rounded font-medium">off{c.service_resume_on ? ` until ${c.service_resume_on}` : ''}</span>
                      )}
                    </div>
                    <div className="flex gap-3 mt-0.5 flex-wrap">
                      {c.phone && <span className="flex items-center gap-1 text-xs text-gray-500"><Phone className="h-3 w-3" />{c.phone}</span>}
                      {c.phone && (
                        <Link to={`/texts/${String(c.phone).replace(/\D/g, '').slice(-10)}`} onClick={e => e.stopPropagation()}
                          className="flex items-center gap-1 text-xs font-semibold text-brand-700 hover:underline" title="Texts with them, in the app">
                          <MessageSquare className="h-3 w-3" />Text
                        </Link>
                      )}
                      {c.email && <span className="flex items-center gap-1 text-xs text-gray-500"><Mail className="h-3 w-3" />{c.email}</span>}
                    </div>
                    <div className="flex gap-3 mt-0.5 flex-wrap text-xs text-gray-500">
                      {c.addresses && (
                        <span className="flex items-center gap-1 min-w-0"><MapPin className="h-3 w-3 flex-shrink-0" /><span className="truncate max-w-[16rem] sm:max-w-md">{c.addresses.split(' | ')[0]}{(c.property_count || 0) > 1 ? ` +${(c.property_count || 1) - 1}` : ''}</span></span>
                      )}
                      {c.mowing_total ? <span className="text-green-700 font-medium">${Number(c.mowing_total).toFixed(0)}/mow</span> : null}
                      {c.last_service_date && <span>last {fmtShortDate(c.last_service_date)}</span>}
                      {c.next_visit_date && <span className="text-brand-700">next {fmtShortDate(c.next_visit_date)}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 ml-4 flex-shrink-0">
                    <button onClick={e => { e.stopPropagation(); setProfileCust(c); }}
                      className="p-1.5 text-gray-500 hover:text-brand-600 rounded" title="View profile">
                      <ExternalLink className="h-4 w-4" />
                    </button>
                    {activeTab === 'archived' ? (
                      <button disabled={readOnly} onClick={e => { e.stopPropagation(); restoreCust.mutate(c.id); }}
                        className="inline-flex items-center gap-1 text-xs font-medium text-brand-600 hover:text-brand-700 border border-brand-300 rounded-md px-2.5 py-1.5 bg-white">
                        <ArchiveRestore className="h-3.5 w-3.5" /> Restore
                      </button>
                    ) : (
                      <>
                        <button onClick={e => { e.stopPropagation(); openEditCust(c); }}
                          className="p-1.5 text-gray-500 hover:text-brand-600 rounded" title="Edit"><Edit2 className="h-4 w-4" /></button>
                        <button disabled={readOnly} onClick={e => {
                          e.stopPropagation();
                          if (window.confirm(`Archive ${c.name}? History is preserved. Future scheduled jobs will be cancelled.`))
                            archiveCust.mutate(c.id);
                        }} className="p-1.5 text-gray-500 hover:text-orange-500 rounded" title="Archive"><Archive className="h-4 w-4" /></button>
                        <button disabled={readOnly} onClick={e => {
                          e.stopPropagation();
                          if (window.confirm(`Permanently delete ${c.name}? Cannot be undone. Archive instead if they have job history.`))
                            deleteCust.mutate(c.id);
                        }} className="p-1.5 text-gray-500 hover:text-red-500 rounded" title="Permanently delete"><Trash2 className="h-4 w-4" /></button>
                      </>
                    )}
                    <ChevronDown className="h-4 w-4 text-gray-300 -rotate-90" />
                  </div>
                </div>

              </div>
            );
          })}
        </div>
      )}

      {/* ── New / Edit Customer modal ── */}
      {custModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <div className={`bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-hidden flex flex-col`}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 flex-shrink-0">
              <h3 className="text-base font-black uppercase tracking-tight text-gray-900">
                {custModal === 'edit' ? 'Edit Customer' : 'New Customer'}
              </h3>
              <button onClick={() => { setCustModal(null); setEditingCust(null); }}>
                <X className="h-5 w-5 text-gray-500 hover:text-gray-600" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-4">
              {custModal === 'new' ? (
                <CustomerIntake
                  prefill={intakePrefill || undefined}
                  leadId={intakePrefill?.lead_id || null}
                  source={intakePrefill?.source || 'direct'}
                  onCancel={() => { setCustModal(null); setIntakePrefill(null); }}
                  onSaved={out => {
                    qc.invalidateQueries('customers'); qc.invalidateQueries('all-properties'); qc.invalidateQueries('leads');
                    setCustModal(null); setIntakePrefill(null);
                    if (out.customer) setJustMade({ customer: out.customer, property: out.property, area: out.area });
                  }} />
              ) : (
                /* Edit mode: single column */
                <div className="space-y-4">
                  <Field label="Company Name">
                    <input value={custForm.company_name} onChange={cf('company_name')} className="input" placeholder="Acme Corp (optional)" />
                  </Field>
                  <div className="grid grid-cols-3 gap-2">
                    <Field label="Title">
                      <select value={custForm.title} onChange={cf('title')} className="input">
                        {TITLES.map(t => <option key={t} value={t}>{t || '—'}</option>)}
                      </select>
                    </Field>
                    <div className="col-span-2">
                      <div className="grid grid-cols-2 gap-3">
                        <Field label="First name *"><input value={custForm.first_name} onChange={cf('first_name')} className="input" placeholder="First" /></Field>
                        <Field label="Last name"><input value={custForm.last_name} onChange={cf('last_name')} className="input" placeholder="Last" /></Field>
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Phone"><input value={custForm.phone} onChange={cf('phone')} className="input" placeholder="(512) 555-0100" /></Field>
                    <Field label="Email"><input type="email" value={custForm.email} onChange={cf('email')} className="input" placeholder="email@example.com" /></Field>
                  </div>
                  <Field label="How did they hear about us?">
                    <select value={custForm.how_heard} onChange={cf('how_heard')} className="input">
                      <option value="">— Select —</option>
                      {HOW_HEARD.map(h => <option key={h} value={h}>{h}</option>)}
                    </select>
                  </Field>
                  <Field label="Notes"><textarea value={custForm.notes} onChange={cf('notes')} className="input" rows={2} /></Field>
                  <div className="border-t border-gray-100 pt-3">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Communication Preferences</p>
                    <div className="space-y-2">
                      <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                        <input type="checkbox" checked={!!custForm.transactional_messages_enabled}
                          onChange={e => setCustForm((f: any) => ({ ...f, transactional_messages_enabled: e.target.checked }))}
                          className="rounded text-brand-600 focus:ring-brand-500" />
                        Send job updates (scheduled, started, completed)
                      </label>
                      <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                        <input type="checkbox" checked={!!custForm.sms_opt_in}
                          onChange={e => setCustForm((f: any) => ({ ...f, sms_opt_in: e.target.checked }))}
                          className="rounded text-brand-600 focus:ring-brand-500" />
                        SMS opt-in (phone required)
                      </label>
                      <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                        <input type="checkbox" checked={!!custForm.marketing_opt_in}
                          onChange={e => setCustForm((f: any) => ({ ...f, marketing_opt_in: e.target.checked }))}
                          className="rounded text-brand-600 focus:ring-brand-500" />
                        Marketing opt-in (promotions, offers)
                      </label>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {custModal === 'edit' && (
              <div className="flex justify-end gap-3 px-5 py-4 border-t border-gray-100 flex-shrink-0">
                <button onClick={() => { setCustModal(null); setEditingCust(null); }} className="btn-secondary">Cancel</button>
                <button onClick={() => saveCust.mutate()} disabled={readOnly || (saveCust.isLoading)} className="btn-primary">
                  {saveCust.isLoading ? 'Saving…' : 'Save Changes'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* After-create: a quote visit, a job, or nothing yet */}
      {justMade && !custModal && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/40" onClick={() => setJustMade(null)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6" onClick={e => e.stopPropagation()}>
            <WhatsNext customer={justMade.customer} property={justMade.property} area={justMade.area} turf={justMade.turf} onDone={() => setJustMade(null)} />
          </div>
        </div>
      )}

      {/* Property modal */}
      {propModal && (
        <PropertyModal
          propModal={propModal}
          propForm={propForm}
          setPropForm={setPropForm}
          onClose={() => setPropModal(null)}
          onSave={() => saveProp.mutate()}
          saving={saveProp.isLoading}
        />
      )}

      {/* CSV Import modal */}
      {csvModal && (
        <CsvImportModal
          existingCustomers={custData || []}
          onClose={() => setCsvModal(false)}
          onImported={() => { qc.invalidateQueries('customers'); qc.invalidateQueries('all-properties'); setCsvModal(false); }}
        />
      )}

      {/* Customer profile modal */}
      {profileCust && (
        <CustomerProfileModal
          customer={profileCust}
          propsFor={propsFor}
          onClose={() => setProfileCust(null)}
          onEdit={() => { openEditCust(profileCust); setProfileCust(null); }}
          onAddProp={() => { openNewProp(profileCust.id); setProfileCust(null); }}
          onEditProp={(prop) => { openEditProp(profileCust.id, prop); setProfileCust(null); }}
          onDeleteProp={(id) => { if (window.confirm('Remove property?')) deleteProp.mutate(id); }}
          navigate={navigate}
        />
      )}
    </div>
  );
}

// ── Customer profile modal ────────────────────────────────────────────────────
const JOB_STATUS_COLORS: Record<string, string> = {
  scheduled: 'bg-brand-100 text-brand-700', complete: 'bg-green-100 text-green-700',
  skipped: 'bg-gray-100 text-gray-500', canceled: 'bg-red-100 text-red-600',
  'in progress': 'bg-blue-100 text-blue-700',
};
const INV_STATUS_COLORS: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-600', sent: 'bg-blue-100 text-blue-700',
  paid: 'bg-green-100 text-green-700', overdue: 'bg-red-100 text-red-600',
  void: 'bg-gray-100 text-gray-500',
};

function CustomerProfileModal({ customer, propsFor, onClose, onEdit, onAddProp, onEditProp, onDeleteProp, navigate }: {
  customer: Customer; propsFor: (id: number) => Property[];
  onClose: () => void; onEdit: () => void; onAddProp: () => void;
  onEditProp: (prop: Property) => void; onDeleteProp: (id: number) => void;
  navigate: (path: string, opts?: any) => void;
}) {
  const readOnly = useReadOnly();   // view-only login: grey out what can only ever be refused
  const [tab, setTab] = useState<'properties' | 'jobs' | 'invoices' | 'quotes' | 'projects'>('properties');
  const props = propsFor(customer.id);
  const qc = useQueryClient();
  const reviewedToggle = useMutation(
    (on: boolean) => on ? api.post(`/customers/${customer.id}/reviewed`, { note: 'marked by hand' }) : api.delete(`/customers/${customer.id}/reviewed`),
    {
      onSuccess: (r: any) => { toast.success(r.data?.message || 'Saved'); qc.invalidateQueries('customers'); },
      onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not save that'); },
    }
  );
  const [cancelProps, setCancelProps] = useState<Property[] | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const cancelledProps = props.filter(p => p.service_status === 'cancelled');
  const activeProps = props.filter(p => p.service_status !== 'cancelled');
  const refreshService = () => {
    qc.invalidateQueries('customers'); qc.invalidateQueries('all-properties');
    qc.invalidateQueries(['cust-profile-jobs', customer.id]); qc.invalidateQueries('routes');
    qc.invalidateQueries(['service-events', customer.id]);
    qc.invalidateQueries(['service-summary', customer.id]);
  };
  const reactivate = useMutation(
    (propertyId?: number) => api.post(`/customers/${customer.id}/reactivate-service`, propertyId ? { property_id: propertyId } : {}),
    { onSuccess: (r: any) => { toast.success(r.data?.message || 'Service reactivated'); refreshService(); },
      onError: (e: any) => { toast.error(e.response?.data?.message || 'Could not reactivate'); } }
  );

  // "I messed up their lawn — the next mow is on us." Finds their next mow,
  // says which one, asks why (office only), then makes it free.
  const freeNextMow = useMutation(
    (reason: string) => api.post(`/customers/${customer.id}/free-next-mow`, { reason }).then(r => r.data),
    { onSuccess: (d: any) => { toast.success(d?.message || 'Next mow is free'); qc.invalidateQueries(['cust-profile-jobs', customer.id]); qc.invalidateQueries('jobs-list'); },
      onError: (e: any) => { toast.error(e.response?.data?.message || 'Could not make the next mow free'); } }
  );
  const askFreeNextMow = async () => {
    try {
      const { data } = await api.get(`/customers/${customer.id}/next-mow`);
      const next = data?.job;
      if (!next) { toast.info('No mow is scheduled for them yet. Schedule one, then use "Free visit" on the job.'); return; }
      const price = Number(next.price_snapshot || 0).toFixed(2);
      const reason = window.prompt(`Make their ${next.day} mow free ($${price} on us)? Why? (office only; their invoice just says "on us")`, '');
      if (reason !== null) freeNextMow.mutate(reason.trim());
    } catch (e: any) { toast.error(e.response?.data?.message || 'Could not find their next mow'); }
  };

  const { data: jobsData, isLoading: loadingJobs } = useQuery(
    ['cust-profile-jobs', customer.id],
    () => api.get('/jobs', { params: { customer_id: customer.id, include_estimates: 1 } }).then(r => r.data.jobs as any[]),
    { enabled: tab === 'jobs' }
  );
  const { data: invoicesData, isLoading: loadingInvoices } = useQuery(
    ['cust-profile-invoices', customer.id],
    () => api.get('/invoices').then(r => (r.data.invoices as any[]).filter((i: any) => i.customer_id === customer.id)),
    { enabled: tab === 'invoices' }
  );
  const { data: quotesData, isLoading: loadingQuotes } = useQuery(
    ['cust-profile-quotes', customer.id],
    () => api.get('/quotes').then(r => (r.data.quotes as any[]).filter((q: any) => q.customer_id === customer.id)),
    { enabled: tab === 'quotes' }
  );
  const { data: projectsData, isLoading: loadingProjects } = useQuery(
    ['cust-profile-projects', customer.id],
    () => api.get('/projects', { params: { customer_id: customer.id } }).then(r => [
      ...(r.data.projects as any[]),
      // Owner rule: any non-mowing job is a project — list them here too.
      ...((r.data.job_projects || []) as any[]).map((j: any) => ({ ...j, kind: 'job', status: j.status === 'complete' ? 'complete' : j.status })),
    ]),
    { enabled: tab === 'projects' }
  );

  const jobs = jobsData || [];
  const invoices = invoicesData || [];
  const quotes = quotesData || [];
  const projects = projectsData || [];

  const TABS = [
    { key: 'properties' as const, label: 'Properties', icon: MapPin },
    { key: 'jobs' as const, label: 'Jobs', icon: Briefcase },
    { key: 'invoices' as const, label: 'Invoices', icon: FileText },
    { key: 'quotes' as const, label: 'Quotes', icon: ClipboardList },
    { key: 'projects' as const, label: 'Projects', icon: HardHat },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/40">
      <div className="bg-white rounded-t-2xl sm:rounded-xl shadow-xl w-full max-w-3xl max-h-[94vh] sm:max-h-[90vh] overflow-hidden flex flex-col">
        {/* Only the name row stays put; the rest scrolls as one — on a phone the header alone used to fill the screen and nothing below it could be reached (owner, Sep 18 2026). */}
        <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <h2 className="text-xl font-bold text-gray-900 truncate">{customer.display_name || customer.name}<AccountMark mark={customer.account_mark} /></h2>
              {customer.company_name && customer.name && customer.company_name !== customer.name && (
                <p className="text-sm text-gray-500 mt-0.5">Contact: {customer.title ? `${customer.title} ` : ''}{customer.name}</p>
              )}
              <div className="flex gap-3 mt-1 flex-wrap">
                {customer.phone && (
                  <span className="flex items-center gap-1 text-sm text-gray-500">
                    <Phone className="h-3.5 w-3.5" />{customer.phone}
                  </span>
                )}
                {customer.phone && (
                  <Link to={`/texts/${String(customer.phone).replace(/\D/g, '').slice(-10)}`}
                    className="flex items-center gap-1 text-sm font-semibold text-brand-700 hover:underline" title="Texts with them, in the app">
                    <MessageSquare className="h-3.5 w-3.5" />Text
                  </Link>
                )}
                {customer.email && (
                  <span className="flex items-center gap-1 text-sm text-gray-500">
                    <Mail className="h-3.5 w-3.5" />{customer.email}
                  </span>
                )}
              </div>
              {customer.notes && (
                <p className="text-sm text-gray-500 italic mt-1 flex items-start gap-1">
                  <StickyNote className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />{customer.notes}
                </p>
              )}
            </div>
            <button onClick={onClose} className="flex-shrink-0 p-1">
              <X className="h-5 w-5 text-gray-500 hover:text-gray-600" />
            </button>
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
        <div className="px-4 sm:px-6 pt-3 pb-2 border-b border-gray-200">
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => { onClose(); navigate(`/calendar?customer=${customer.id}`); }}
              className="press inline-flex items-center gap-1.5 text-xs font-medium text-black bg-brand-600 hover:bg-brand-500 rounded-xl px-3 py-1.5">
              <Calendar className="h-3.5 w-3.5" /> Create Job
            </button>
            <button
              onClick={() => { onClose(); navigate('/billing', { state: { newInvoiceFor: { id: customer.id, name: customer.name } } }); }}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-700 border border-gray-300 hover:bg-gray-50 rounded-md px-3 py-1.5 bg-white">
              <FileText className="h-3.5 w-3.5" /> Invoice Customer
            </button>
            <button
              onClick={() => { onClose(); navigate('/quotes'); }}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-700 border border-gray-300 hover:bg-gray-50 rounded-md px-3 py-1.5 bg-white">
              <ClipboardList className="h-3.5 w-3.5" /> Quote Customer
            </button>
            {/* Already reviewed us → the portal, invoice page and discounts stop asking. */}
            <button
              onClick={() => reviewedToggle.mutate(!customer.reviewed_at)}
              disabled={readOnly || (reviewedToggle.isLoading)}
              title={customer.reviewed_at ? `Marked reviewed${customer.reviewed_note ? ` — ${customer.reviewed_note}` : ''}. Click to clear.` : 'Mark that they already left a Google review'}
              className={`inline-flex items-center gap-1.5 text-xs font-medium rounded-md px-3 py-1.5 border ${customer.reviewed_at ? 'text-green-700 bg-green-50 border-green-200' : 'text-gray-500 bg-white border-gray-300 hover:bg-gray-50'} disabled:opacity-50`}>
              {customer.reviewed_at ? '✓ Left a review' : 'Left a review?'}
            </button>
            <button
              onClick={onAddProp}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-700 border border-gray-300 hover:bg-gray-50 rounded-md px-3 py-1.5 bg-white">
              <MapPin className="h-3.5 w-3.5" /> Add Property
            </button>
            <button
              onClick={onEdit}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-700 border border-gray-300 hover:bg-gray-50 rounded-md px-3 py-1.5 bg-white">
              <Edit2 className="h-3.5 w-3.5" /> Edit Info
            </button>
            {activeProps.length > 0 && (
              <button
                onClick={askFreeNextMow}
                disabled={freeNextMow.isLoading}
                className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700 border border-emerald-200 hover:bg-emerald-50 rounded-md px-3 py-1.5 bg-white disabled:opacity-50">
                <Gift className="h-3.5 w-3.5" /> Free next mow
              </button>
            )}
            {activeProps.length > 0 && (
              <button
                onClick={() => setCancelProps(activeProps)}
                className="inline-flex items-center gap-1.5 text-xs font-medium text-red-700 border border-red-200 hover:bg-red-50 rounded-md px-3 py-1.5 bg-white">
                <X className="h-3.5 w-3.5" /> Cancel service
              </button>
            )}
            {cancelledProps.length > 0 && (
              <button
                onClick={() => { if (window.confirm(`Reactivate service for ${customer.display_name || customer.name}? Their schedules come back and visits are generated from today.`)) reactivate.mutate(undefined); }}
                disabled={readOnly || (reactivate.isLoading)}
                className="inline-flex items-center gap-1.5 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded-md px-3 py-1.5 disabled:opacity-50">
                <ArchiveRestore className="h-3.5 w-3.5" /> {reactivate.isLoading ? 'Reactivating…' : 'Reactivate service'}
              </button>
            )}
          </div>

          {cancelledProps.length > 0 && (
            <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
              <p className="font-semibold">Service cancelled{cancelledProps.length < props.length ? ` on ${cancelledProps.length} of ${props.length} properties` : ''}</p>
              {cancelledProps.map(p => (
                <p key={p.id} className="text-xs">{p.address}: from {p.service_cancelled_effective}{p.service_cancel_reason ? ` — ${p.service_cancel_reason}` : ''}</p>
              ))}
              <button onClick={() => setShowHistory(v => !v)} className="text-xs underline mt-1">{showHistory ? 'Hide history' : 'History'}</button>
              {showHistory && <ServiceHistory customerId={customer.id} />}
            </div>
          )}

          <PaymentAutopayBlock customerId={customer.id} />
          {/* Owner, Oct 26 2026: the bids they signed and the bills they paid, right on the card. */}
          <SignedAndPaidBlock customerId={customer.id} onNavigate={onClose} />
          <CustomerAppBlock customerId={customer.id} />
          <PlanOptionsBlock customerId={customer.id} />
          <CustomerPhonesBlock customerId={customer.id} />
          <CustomerServiceBlock customerId={customer.id} />
          <CustomerTextsBlock customerId={customer.id} />
          <CustomerGroupsLine customerId={customer.id} customerName={customer.name} />
          <CustomerInterestsBlock customerId={customer.id} customerName={customer.name} />
          <CustomerTodosBlock customerId={customer.id} customerName={customer.name} properties={props} />
        </div>

        <div className="flex border-b border-gray-200 px-4 sm:px-6 overflow-x-auto sticky top-0 bg-white z-10">
          {TABS.map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`flex items-center gap-1.5 px-3 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
                tab === t.key
                  ? 'border-brand-600 text-brand-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <t.icon className="h-4 w-4" />
              {t.label}
            </button>
          ))}
        </div>

        <div className="px-4 sm:px-6 py-4">
          {tab === 'properties' && (
            <div className="space-y-3">
              {props.length === 0
                ? <p className="text-sm text-gray-500 text-center py-8">No properties. Add one to start scheduling jobs.</p>
                : props.map(p => (
                    <PropertyCard key={p.id} prop={p}
                      onEdit={() => onEditProp(p)}
                      onDelete={() => onDeleteProp(p.id)}
                      onSchedule={() => { onClose(); navigate(`/calendar?customer=${customer.id}&property=${p.id}`); }}
                    />
                  ))
              }
            </div>
          )}

          {tab === 'jobs' && (
            <div className="space-y-2">
              <RecurringServiceSummary
                customerId={customer.id}
                onCancel={p => setCancelProps([p])}
                onReactivate={p => { if (window.confirm(`Reactivate service at ${p.address}?`)) reactivate.mutate(p.id); }}
              />
              {loadingJobs && <p className="text-sm text-gray-500 text-center py-8">Loading…</p>}
              {!loadingJobs && jobs.length === 0 && <p className="text-sm text-gray-500 text-center py-8">No jobs for this customer.</p>}
              {jobs.map((j: any) => (
                <div key={j.id} onClick={() => { onClose(); navigate(j.service_type === 'estimate' ? `/quote-visits/${j.id}` : `/jobs/${j.id}`); }}
                  title={j.service_type === 'estimate' ? 'Open this quote visit' : 'Open this job'}
                  className="flex items-center justify-between rounded-lg bg-gray-50 border border-gray-100 px-4 py-2.5 cursor-pointer hover:bg-brand-50">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-gray-900">{j.scheduled_date}</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${JOB_STATUS_COLORS[j.status] || 'bg-gray-100 text-gray-600'}`}>{j.status}</span>
                      {j.service_type === 'estimate' && <span className="text-[10px] font-semibold uppercase tracking-wide text-brand-800">Quote visit</span>}
                    </div>
                    <p className="text-xs text-gray-500 truncate">{j.address || j.service_type}</p>
                  </div>
                  {j.price != null && j.service_type !== 'estimate' && (
                    <span className="text-sm font-semibold text-green-700 ml-2">${Number(j.price).toFixed(2)}</span>
                  )}
                </div>
              ))}
            </div>
          )}

          {tab === 'invoices' && (
            <div className="space-y-2">
              {loadingInvoices && <p className="text-sm text-gray-500 text-center py-8">Loading…</p>}
              {!loadingInvoices && invoices.length === 0 && (
                <div className="text-center py-8">
                  <p className="text-sm text-gray-500 mb-3">No invoices for this customer.</p>
                  <button onClick={() => { onClose(); navigate('/billing', { state: { newInvoiceFor: { id: customer.id, name: customer.name } } }); }}
                    className="text-sm text-brand-600 hover:underline">Create an invoice →</button>
                </div>
              )}
              {invoices.map((inv: any) => (
                <div key={inv.id} onClick={() => { onClose(); navigate('/billing', { state: { openInvoiceId: inv.id } }); }} title="Open this invoice"
                  className="flex items-center justify-between rounded-lg bg-gray-50 border border-gray-100 px-4 py-2.5 cursor-pointer hover:bg-brand-50">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-gray-900">#{inv.invoice_number || inv.id}</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${INV_STATUS_COLORS[inv.status] || 'bg-gray-100 text-gray-600'}`}>{inv.status}</span>
                    </div>
                    {inv.due_date && <p className="text-xs text-gray-500">Due {inv.due_date}</p>}
                  </div>
                  {inv.total != null && (
                    <span className="text-sm font-semibold text-green-700 ml-2">${Number(inv.total).toFixed(2)}</span>
                  )}
                </div>
              ))}
            </div>
          )}

          {tab === 'quotes' && (
            <div className="space-y-2">
              {loadingQuotes && <p className="text-sm text-gray-500 text-center py-8">Loading…</p>}
              {!loadingQuotes && quotes.length === 0 && (
                <div className="text-center py-8">
                  <p className="text-sm text-gray-500 mb-3">No quotes for this customer.</p>
                  <button onClick={() => { onClose(); navigate('/quotes'); }}
                    className="text-sm text-brand-600 hover:underline">Go to Quotes →</button>
                </div>
              )}
              {quotes.map((q: any) => (
                <div key={q.id} onClick={() => { onClose(); navigate('/quotes', { state: { openQuoteId: q.id } }); }} title="Open this quote"
                  className="flex items-center justify-between rounded-lg bg-gray-50 border border-gray-100 px-4 py-2.5 cursor-pointer hover:bg-brand-50">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900 truncate">{q.title}</p>
                    <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium inline-block mt-0.5 ${
                      q.status === 'accepted' ? 'bg-green-100 text-green-700' :
                      q.status === 'sent' ? 'bg-blue-100 text-blue-700' :
                      q.status === 'declined' ? 'bg-red-100 text-red-600' :
                      'bg-gray-100 text-gray-600'
                    }`}>{q.status}</span>
                  </div>
                  {q.total != null && q.total > 0 && (
                    <span className="text-sm font-semibold text-green-700 ml-2">${Number(q.total).toFixed(2)}</span>
                  )}
                </div>
              ))}
            </div>
          )}

          {tab === 'projects' && (
            <div className="space-y-2">
              {loadingProjects && <p className="text-sm text-gray-500 text-center py-8">Loading…</p>}
              {!loadingProjects && projects.length === 0 && (
                <div className="text-center py-8">
                  <p className="text-sm text-gray-500 mb-3">No projects for this customer.</p>
                  <button onClick={() => { onClose(); navigate('/projects'); }}
                    className="text-sm text-brand-600 hover:underline">Go to Projects →</button>
                </div>
              )}
              {projects.map((p: any) => (
                <div key={`${p.kind || 'project'}-${p.id}`} onClick={() => { onClose(); navigate(p.kind === 'job' ? `/jobs/${p.id}` : '/projects'); }} title="Open"
                  className="flex items-center justify-between rounded-lg bg-gray-50 border border-gray-100 px-4 py-2.5 cursor-pointer hover:bg-brand-50">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-gray-900 truncate">{p.title || p.service_type}</span>
                      {p.kind === 'job' && <span className="text-[10px] uppercase tracking-wide text-gray-500">job</span>}
                      <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${
                        p.status === 'complete' ? 'bg-green-100 text-green-700' :
                        p.status === 'in progress' ? 'bg-blue-100 text-blue-700' :
                        p.status === 'canceled' ? 'bg-red-100 text-red-600' :
                        'bg-gray-100 text-gray-600'
                      }`}>{p.status}</span>
                    </div>
                    {p.date_start && <p className="text-xs text-gray-500">{p.date_start}{p.date_end ? ` – ${p.date_end}` : ''}</p>}
                  </div>
                  {p.price != null && (
                    <span className="text-sm font-semibold text-green-700 ml-2">${Number(p.price).toFixed(2)}</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        </div>
      </div>
      {cancelProps && cancelProps.length > 0 && (
        <CancelServiceModal customer={customer} props={cancelProps}
          onClose={() => setCancelProps(null)}
          onDone={() => { setCancelProps(null); refreshService(); }} />
      )}
    </div>
  );
}

// ── Recurring service at a glance: mowing · $$ · est · next · last · cancel ──
function RecurringServiceSummary({ customerId, onCancel, onReactivate }: {
  customerId: number; onCancel: (p: Property) => void; onReactivate: (p: Property) => void;
}) {
  const { data, isLoading } = useQuery(['service-summary', customerId], () =>
    api.get(`/customers/${customerId}/service-summary`).then(r => r.data.properties as any[]));
  const fmtD = (iso?: string | null) => iso ? new Date(String(iso).slice(0, 10) + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : '—';
  if (isLoading) return <p className="text-xs text-gray-500">Loading recurring service…</p>;
  const props = (data || []).filter(p => p.schedules.length > 0 || p.service_status === 'cancelled');
  if (props.length === 0) return null;
  return (
    <div className="space-y-2 mb-3">
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Recurring service</p>
      {props.map((p: any) => {
        const cancelled = p.service_status === 'cancelled';
        const active = p.schedules.filter((s: any) => s.is_active);
        return (
          <div key={p.id} className={`rounded-xl border px-4 py-3 ${cancelled ? 'border-red-200 bg-red-50' : 'border-emerald-200 bg-emerald-50/60'}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-900">{p.label && !/^main property$/i.test(p.label) ? `${p.label} — ` : ''}{p.address}</p>
                {cancelled ? (
                  <p className="text-xs text-red-800 mt-0.5">
                    <span className="font-semibold">Service cancelled</span> from {fmtD(p.service_cancelled_effective)}{p.service_cancel_reason ? ` — ${p.service_cancel_reason}` : ''}
                  </p>
                ) : active.length === 0 ? (
                  <p className="text-xs text-gray-500 mt-0.5">No active schedule.</p>
                ) : active.map((sch: any) => (
                  <p key={sch.id} className="text-xs text-gray-700 mt-0.5 flex flex-wrap gap-x-2 gap-y-0.5 items-center">
                    <span className={`font-semibold ${sch.title ? '' : 'capitalize'}`}>{sch.title || String(sch.service_type).replace(/_/g, ' ')}</span>
                    {/* Work billed per month on its own rhythm (services/monthlyLine): the visit is covered by the monthly charge, not priced. */}
                    {sch.covered_words ? <span className="px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[10px] font-semibold">{sch.covered_words}</span>
                      : sch.price != null && <span className="text-green-700 font-semibold">${Number(sch.price).toFixed(2)}</span>}
                    {sch.estimated_minutes && <span>{sch.estimated_minutes} min</span>}
                    <span>{sch.cadence}</span>
                    {sch.route_name && <span className="px-1.5 py-0.5 rounded-full text-snow text-[10px]" style={{ backgroundColor: sch.route_color || '#7c3aed' }}>{sch.route_name}</span>}
                    {/* A yard mowed every week rides both routes of its day — naming one beside the other week's date read wrong. */}
                    {sch.other_week_route_name && <><span>and</span><span className="px-1.5 py-0.5 rounded-full text-snow text-[10px]" style={{ backgroundColor: sch.other_week_route_color || sch.route_color || '#7c3aed' }}>{sch.other_week_route_name}</span><span>(both weeks)</span></>}
                    <span className="text-brand-700 font-medium">next {fmtD(sch.next_date)}</span>
                  </p>
                ))}
                <p className="text-[11px] text-gray-500 mt-1">
                  Last service {fmtD(p.last_service_date)}
                  {p.lot_sqft ? ` · lot ${Number(p.lot_sqft).toLocaleString()} sq ft` : ' · lot size not set'}
                  {p.treatable_sqft ? ` · turf ${Number(p.treatable_sqft).toLocaleString()} sq ft${p.turf_source === 'county map' ? ' (county map)' : ''}` : p.turf_assessed_at ? ' · turf: measure on the visit' : ''}
                  {p.cutting_height ? ` · cut ${p.cutting_height}` : ''}
                  {p.last_event && p.last_event.action === 'reactivate' ? ` · reactivated ${fmtD(p.last_event.effective_date)}` : ''}
                </p>
              </div>
              <div className="flex-shrink-0">
                {cancelled ? (
                  <button onClick={() => onReactivate(p)} className="text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded-md px-3 py-1.5 flex items-center gap-1"><ArchiveRestore className="h-3.5 w-3.5" /> Reactivate</button>
                ) : active.length > 0 ? (
                  <button onClick={() => onCancel(p)} className="text-xs font-medium text-red-700 border border-red-200 hover:bg-red-100 rounded-md px-3 py-1.5 bg-white flex items-center gap-1"><X className="h-3.5 w-3.5" /> Cancel service</button>
                ) : null}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Service history (cancel / reactivate audit trail) ─────────────────────────
function ServiceHistory({ customerId }: { customerId: number }) {
  const { data, isLoading } = useQuery(['service-events', customerId], () =>
    api.get(`/customers/${customerId}/service-events`).then(r => r.data.events as any[]));
  const events = data || [];
  return (
    <div className="mt-1.5 border-l-2 border-red-200 pl-2 space-y-0.5">
      {isLoading ? <p className="text-xs text-red-600">Loading…</p>
        : events.length === 0 ? <p className="text-xs text-red-600">No history.</p>
        : events.map((ev: any) => (
          <p key={ev.id} className="text-xs text-red-900">
            <span className="opacity-60">{String(ev.created_at || '').slice(0, 10)}</span> · <span className="font-semibold capitalize">{ev.action}</span>
            {ev.effective_date ? ` from ${ev.effective_date}` : ''}{ev.address ? ` · ${ev.address}` : ''}
            {ev.reason ? ` — ${ev.reason}` : ''}{ev.actor_name ? ` (${ev.actor_name})` : ''}
            {ev.action === 'cancel' && ev.payload ? ` · ${ev.payload.schedules?.length || 0} schedule(s), ${ev.payload.jobs?.length || 0} visit(s) removed` : ''}
            {ev.send_message ? ` · message: ${ev.message_result?.sent ? 'sent' : 'not sent (messaging not live)'}` : ''}
          </p>
        ))}
    </div>
  );
}

// ── Cancel service modal ──────────────────────────────────────────────────────
function CancelServiceModal({ customer, props, onClose, onDone }: {
  customer: Customer; props: Property[]; onClose: () => void; onDone: () => void;
}) {
  const readOnly = useReadOnly();   // view-only login: grey out what can only ever be refused
  const [propertyId, setPropertyId] = useState<string>(props.length === 1 ? String(props[0].id) : '');
  const [effective, setEffective] = useState(new Date().toLocaleDateString('en-CA', { timeZone: 'America/Chicago' }));
  const [reason, setReason] = useState('');
  const [sendMessage, setSendMessage] = useState(false);
  const cancel = useMutation(
    () => api.post(`/customers/${customer.id}/cancel-service`, {
      effective_date: effective, reason: reason.trim() || undefined,
      property_id: propertyId ? Number(propertyId) : undefined, send_message: sendMessage,
    }),
    { onSuccess: (r: any) => { toast.success(r.data?.message || 'Service cancelled'); onDone(); },
      onError: (e: any) => { toast.error(e.response?.data?.message || 'Could not cancel service'); } }
  );
  return (
    <Modal title={`Cancel service — ${customer.display_name || customer.name}`} onClose={onClose}>
      <div className="space-y-3">
        <p className="text-sm text-gray-600">Stops their recurring visits from the date below. Completed work, invoices and pay stay exactly as they are. You can reactivate later.</p>
        {props.length > 1 && (
          <Field label="Which property">
            <select value={propertyId} onChange={e => setPropertyId(e.target.value)} className="input">
              <option value="">All properties</option>
              {props.map(p => <option key={p.id} value={p.id}>{p.label && !/^main property$/i.test(p.label) ? `${p.label} — ` : ''}{p.address}</option>)}
            </select>
          </Field>
        )}
        <Field label="Effective date *">
          <input type="date" value={effective} onChange={e => setEffective(e.target.value)} className="input" />
        </Field>
        <Field label="Reason (optional)">
          <textarea value={reason} onChange={e => setReason(e.target.value)} rows={2} className="input" placeholder="Moving, price, switching providers…" />
        </Field>
        <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
          <input type="checkbox" checked={sendMessage} onChange={e => setSendMessage(e.target.checked)} className="rounded text-brand-600 mt-0.5" />
          <span>Also send the customer a message <span className="text-xs text-gray-500">(messaging isn't live yet — this is recorded, nothing is sent)</span></span>
        </label>
      </div>
      <div className="flex justify-end gap-3 mt-4">
        <button onClick={onClose} className="btn-secondary">Keep service</button>
        <button onClick={() => cancel.mutate()} disabled={readOnly || (cancel.isLoading || !effective)}
          className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-50">
          {cancel.isLoading ? 'Cancelling…' : `Cancel service from ${effective || '…'}`}
        </button>
      </div>
    </Modal>
  );
}

// ── Property modal with autocomplete ─────────────────────────────────────────
function PropertyModal({ propModal, propForm, setPropForm, onClose, onSave, saving }: {
  propModal: any; propForm: any; setPropForm: any;
  onClose: () => void; onSave: () => void; saving: boolean;
}) {
  const [geocoding, setGeocoding] = useState(false);
  const [geocodeFailed, setGeocodeFailed] = useState(false);
  const [showManual, setShowManual] = useState(false);

  const pf = (k: string) => (e: React.ChangeEvent<any>) => {
    const v = e.target.value;
    if (['address', 'city'].includes(k)) {
      setPropForm((f: any) => ({ ...f, [k]: v, lat: null, lng: null }));
      setGeocodeFailed(false);
    } else {
      setPropForm((f: any) => ({ ...f, [k]: v }));
    }
  };

  async function geocode() {
    const addr = [propForm.address, propForm.city, propForm.state, propForm.zip_code].filter(Boolean).join(', ');
    if (!addr.trim()) return;
    setGeocoding(true); setGeocodeFailed(false);
    try {
      const r = await api.get('/properties/geocode', { params: { address: addr } });
      if (r.data.found) {
        setPropForm((f: any) => ({ ...f, lat: r.data.lat, lng: r.data.lng }));
        setGeocodeFailed(false);
      } else {
        setGeocodeFailed(true);
        setShowManual(true);
      }
    } catch {
      setGeocodeFailed(true);
      setShowManual(true);
    } finally {
      setGeocoding(false);
    }
  }

  const hasCoords = !!(propForm.lat && propForm.lng);

  return (
    <Modal title={propModal.prop ? 'Edit Property' : 'Add Property'} onClose={onClose}>
      <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-1">
        <Field label="Property Label">
          <input value={propForm.label} onChange={pf('label')} className="input" placeholder="e.g. Home, Ranch, Rental #2" />
        </Field>

        <div>
          <label className="label-sm">Address *</label>
          <AddressAutocomplete
            value={propForm.address}
            onChange={(v) => {
              setPropForm((f: any) => ({ ...f, address: v, lat: null, lng: null }));
              setGeocodeFailed(false);
            }}
            onSelect={(s) => {
              setPropForm((f: any) => ({
                ...f,
                address: s.address,
                city: s.city || f.city,
                state: s.state || f.state || 'TX',
                zip_code: s.zip || f.zip_code,
                lat: s.lat,
                lng: s.lng,
              }));
              setGeocodeFailed(false);
            }}
            placeholder="123 Main St"
          />
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-2">
            <label className="label-sm">City</label>
            <input value={propForm.city} onChange={pf('city')} className="input" />
          </div>
          <Field label="Zip"><input value={propForm.zip_code} onChange={pf('zip_code')} className="input" placeholder="78701" /></Field>
        </div>

        {/* Lot size — the only thing a mowing price is worked out from (Settings → Automatic quotes → bands). */}
        <LotSizeRow propForm={propForm} setPropForm={setPropForm} prop={propModal.prop} />

        {/* Coordinate status row */}
        <div className="flex items-center gap-2 -mt-1">
          {geocoding ? (
            <span className="text-xs text-gray-500 flex items-center gap-1">
              <Crosshair className="h-3 w-3 animate-spin" /> Looking up coordinates…
            </span>
          ) : hasCoords ? (
            <span className="text-xs text-green-600 flex items-center gap-1">
              <CheckCircle className="h-3 w-3" />
              Coordinates saved ({(propForm.lat as number).toFixed(4)}, {(propForm.lng as number).toFixed(4)})
            </span>
          ) : geocodeFailed ? (
            <span className="text-xs text-orange-600 flex items-center gap-1">
              <AlertCircle className="h-3 w-3" />
              Could not geocode — check address or enter manually
            </span>
          ) : propForm.address ? (
            <span className="text-xs text-amber-500 flex items-center gap-1">
              <AlertCircle className="h-3 w-3" />
              Address needs coordinates
            </span>
          ) : null}

          {propForm.address && (
            <button
              type="button"
              onClick={geocode}
              disabled={geocoding}
              title="Refresh coordinates from full address"
              className="ml-auto flex items-center gap-1 text-xs text-brand-600 hover:text-brand-700 disabled:opacity-40"
            >
              <Crosshair className="h-3 w-3" />
              {geocoding ? 'Looking up…' : 'Refresh'}
            </button>
          )}
        </div>

        {/* Manual lat/lng fallback */}
        {(geocodeFailed || showManual || (!hasCoords && propForm.address)) && (
          <div>
            <button
              type="button"
              onClick={() => setShowManual(v => !v)}
              className="text-xs text-gray-500 hover:text-gray-600 underline"
            >
              {showManual ? 'Hide manual entry' : 'Enter coordinates manually'}
            </button>
            {showManual && (
              <div className="grid grid-cols-2 gap-3 mt-2">
                <div>
                  <label className="label-sm">Latitude</label>
                  <input
                    value={propForm.lat ?? ''}
                    onChange={e => setPropForm((f: any) => ({ ...f, lat: e.target.value ? parseFloat(e.target.value) : null }))}
                    className="input font-mono text-sm"
                    placeholder="30.26715"
                    type="number"
                    step="0.00001"
                  />
                </div>
                <div>
                  <label className="label-sm">Longitude</label>
                  <input
                    value={propForm.lng ?? ''}
                    onChange={e => setPropForm((f: any) => ({ ...f, lng: e.target.value ? parseFloat(e.target.value) : null }))}
                    className="input font-mono text-sm"
                    placeholder="-97.74306"
                    type="number"
                    step="0.00001"
                  />
                </div>
              </div>
            )}
          </div>
        )}

        <div className="border-t border-gray-100 pt-4">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Mowing Details</p>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Mowing Price ($)">
              <input type="number" value={propForm.mowing_price} onChange={pf('mowing_price')} className="input" placeholder="0.00" step="0.01" />
            </Field>
            <Field label="Est. Time (minutes)">
              <input type="number" value={propForm.estimated_minutes} onChange={pf('estimated_minutes')} className="input" placeholder="30" />
            </Field>
          </div>
          <Field label="Travel charge per mow ($)">
            <input type="number" value={propForm.travel_surcharge ?? ''} onChange={pf('travel_surcharge')} className="input" placeholder="0" step="1" min="0" />
            <p className="text-[11px] text-gray-500 mt-1">
              {propForm.travel_miles_out ? `Set when the yard was added — ${Math.ceil(Number(propForm.travel_miles_out))} miles outside the service area. ` : 'Yards outside the service area get this on the day they are added. '}
              Its own line on the bid and the month-end bill. Blank or 0 = none.
            </p>
          </Field>
          <Field label="Cutting Height">
            <select value={propForm.cutting_height} onChange={pf('cutting_height')} className="input">
              <option value="">— Not set —</option>
              {CUTTING_HEIGHTS.map(h => <option key={h} value={h}>{h}</option>)}
            </select>
          </Field>
          <Field label="Fuel this yard takes">
            <select value={propForm.fuel_level || 'normal'} onChange={pf('fuel_level')} className="input">
              <option value="light">Light — small yard, push mower</option>
              <option value="normal">Normal — most yards</option>
              <option value="heavy">Heavy — big property, ride-on, lots of trimming</option>
            </select>
          </Field>
          <Field label="Crew Notes (shown on every job)">
            <textarea value={propForm.crew_notes} onChange={pf('crew_notes')} className="input" rows={2}
              placeholder="e.g. Dog in back yard. Use side gate code 1234." />
          </Field>
        </div>

        <div className="border-t border-gray-100 pt-4">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Access</p>
          <Field label="Gate Notes"><input value={propForm.gate_notes} onChange={pf('gate_notes')} className="input" placeholder="Code, unlocked, etc." /></Field>
          <Field label="Access Notes"><input value={propForm.access_notes} onChange={pf('access_notes')} className="input" placeholder="Dog, ring bell, side entrance, etc." /></Field>
          <Field label="Property Notes"><textarea value={propForm.notes} onChange={pf('notes')} className="input" rows={2} /></Field>
        </div>
      </div>
      <div className="pt-4 border-t border-gray-100 mt-4">
        <ModalFooter onCancel={onClose} onSave={onSave} saving={saving} label={propModal.prop ? 'Save Property' : 'Add Property'} />
      </div>
    </Modal>
  );
}

// ── CSV Import modal ──────────────────────────────────────────────────────────
function CsvImportModal({ existingCustomers, onClose, onImported }: {
  existingCustomers: Customer[];
  onClose: () => void;
  onImported: () => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [rows, setRows] = useState<Record<string, string>[]>([]);
  const [fileName, setFileName] = useState('');
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<any>(null);

  const existingEmails = new Set(existingCustomers.map(c => c.email?.toLowerCase()).filter(Boolean));
  const existingPhones = new Set(existingCustomers.map(c => c.phone?.replace(/\D/g, '')).filter(Boolean));

  interface AnalyzedRow {
    [key: string]: any;
    _row: number;
    _invalid: boolean;
    _duplicate: boolean;
    _reason: string;
  }

  const analyzed: AnalyzedRow[] = rows.map((r, i) => {
    const name = (r.customer_name || r.name || '').trim();
    const email = r.email?.toLowerCase().trim();
    const phone = r.phone?.replace(/\D/g, '');
    const invalid = !name;
    const duplicate = !!(email && existingEmails.has(email)) || !!(phone && phone.length >= 7 && existingPhones.has(phone));
    const reason = invalid ? 'Missing name' : duplicate ? 'Duplicate (email or phone matches existing)' : '';
    return { ...r, _row: i + 1, _invalid: invalid, _duplicate: duplicate, _reason: reason };
  });

  const validCount = analyzed.filter(r => !r._invalid && !r._duplicate).length;
  const invalidCount = analyzed.filter(r => r._invalid).length;
  const dupCount = analyzed.filter(r => !r._invalid && r._duplicate).length;

  function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = ev => {
      const parsed = parseCSV(ev.target?.result as string);
      setRows(parsed);
      setResult(null);
    };
    reader.readAsText(file);
  }

  async function doImport() {
    if (!rows.length) return;
    setImporting(true);
    try {
      const r = await api.post('/customers/import', { rows });
      setResult(r.data);
      if (r.data.created > 0) { toast.success(r.data.message); onImported(); }
      else toast.info(r.data.message);
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'Import failed');
    } finally {
      setImporting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h3 className="text-base font-black uppercase tracking-tight text-gray-900">Import Customers from CSV</h3>
          <button onClick={onClose}><X className="h-5 w-5 text-gray-500 hover:text-gray-600" /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-xs font-semibold text-gray-600 mb-1">Expected CSV columns (first row = headers):</p>
            <p className="text-xs text-gray-500 font-mono">{CSV_COLUMNS.join(', ')}</p>
          </div>

          <div>
            <input ref={fileRef} type="file" accept=".csv,.txt" className="hidden" onChange={handleFile} />
            <button onClick={() => fileRef.current?.click()} className="btn-secondary">
              <Upload className="h-4 w-4 mr-2" /> {fileName || 'Choose CSV file'}
            </button>
          </div>

          {rows.length > 0 && (
            <div className="flex items-center gap-4 text-sm flex-wrap">
              <span className="text-gray-600 font-medium">{rows.length} row{rows.length !== 1 ? 's' : ''} total</span>
              <span className="text-green-700 font-semibold">{validCount} will be imported</span>
              {dupCount > 0 && <span className="text-amber-600">{dupCount} duplicate{dupCount !== 1 ? 's' : ''} (will be skipped)</span>}
              {invalidCount > 0 && <span className="text-red-600">{invalidCount} invalid (missing name)</span>}
            </div>
          )}

          {analyzed.length > 0 && (
            <div className="rounded-lg border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto overflow-y-auto" style={{ maxHeight: 320 }}>
                <table className="text-xs w-full">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-2 py-2 text-left font-medium text-gray-500 w-8">#</th>
                      {['Name', 'Phone', 'Email', 'Address', 'Price', 'Min'].map(h => (
                        <th key={h} className="px-2 py-2 text-left font-medium text-gray-600">{h}</th>
                      ))}
                      <th className="px-2 py-2 text-left font-medium text-gray-500">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {analyzed.map((r) => {
                      const rowClass = r._invalid ? 'bg-red-50' : r._duplicate ? 'bg-amber-50' : '';
                      return (
                        <tr key={r._row} className={rowClass}>
                          <td className="px-2 py-1.5 text-gray-500">{r._row}</td>
                          <td className="px-2 py-1.5 text-gray-900 font-medium">{r.customer_name || r.name}</td>
                          <td className="px-2 py-1.5 text-gray-600">{r.phone}</td>
                          <td className="px-2 py-1.5 text-gray-600">{r.email}</td>
                          <td className="px-2 py-1.5 text-gray-600 max-w-[120px] truncate">{r.address}</td>
                          <td className="px-2 py-1.5 text-gray-600">{r.mowing_price ? `$${r.mowing_price}` : ''}</td>
                          <td className="px-2 py-1.5 text-gray-600">{r.estimated_minutes}</td>
                          <td className="px-2 py-1.5">
                            {r._invalid && <span className="text-red-600 font-medium">Invalid</span>}
                            {!r._invalid && r._duplicate && <span className="text-amber-600">Duplicate</span>}
                            {!r._invalid && !r._duplicate && <span className="text-green-600">✓</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {result && (
            <div className={`rounded-lg p-3 text-sm ${result.errors?.length ? 'bg-orange-50 border border-orange-200' : 'bg-green-50 border border-green-200'}`}>
              <p className={result.errors?.length ? 'text-orange-800 font-medium' : 'text-green-800 font-medium'}>{result.message}</p>
              {result.errors?.map((e: any, i: number) => (
                <p key={i} className="text-xs text-red-600 mt-1">Row — {e.row?.customer_name || e.row?.name || '(unnamed)'}: {e.reason}</p>
              ))}
            </div>
          )}
        </div>

        <div className="px-5 py-4 border-t border-gray-200 flex items-center justify-between gap-3">
          <p className="text-xs text-gray-500">
            {rows.length > 0 ? `${validCount} of ${rows.length} rows ready to import` : 'Select a CSV file to preview'}
          </p>
          <div className="flex gap-3">
            <button onClick={onClose} className="btn-secondary">Cancel</button>
            <button onClick={doImport} disabled={importing || validCount === 0} className="btn-primary">
              {importing ? 'Importing…' : `Import ${validCount} Customer${validCount !== 1 ? 's' : ''}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────
function PropertyCard({ prop, onEdit, onDelete, onSchedule }: {
  prop: Property; onEdit: () => void; onDelete: () => void; onSchedule: () => void;
}) {
  const readOnly = useReadOnly();   // view-only login: grey out what can only ever be refused
  const fullAddr = [prop.address, prop.city, prop.state].filter(Boolean).join(', ');
  return (
    <div className="bg-white rounded-lg border border-gray-200 px-3 py-3">
      <div className="flex items-start gap-2">
        <MapPin className={`h-4 w-4 mt-0.5 flex-shrink-0 ${prop.lat ? 'text-green-500' : 'text-brand-500'}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            {prop.label && <span className="text-xs font-semibold text-brand-700 bg-brand-50 px-1.5 py-0.5 rounded">{prop.label}</span>}
            <span className="text-sm font-medium text-gray-900">{fullAddr}</span>
            {prop.lat && <span className="text-xs text-green-600 font-medium">📍 geocoded</span>}
            {/* Turf from the county map (services/turf) — or the reason to measure it on the visit. */}
            {prop.treatable_sqft
              ? <span className={`text-xs font-medium ${prop.turf_source === 'typed' || !prop.turf_flag ? 'text-emerald-700' : 'text-amber-700'}`}>turf {Number(prop.treatable_sqft).toLocaleString()} sq ft{prop.turf_source === 'county map' ? ' · county map' : prop.turf_source === 'typed' ? ' · typed' : ''}{prop.turf_flag && prop.turf_source !== 'typed' ? ' · check on the visit' : ''}</span>
              : prop.turf_assessed_at ? <span className="text-xs text-amber-700 font-medium">turf: measure on the visit{prop.lot_sqft ? ` · lot ${Number(prop.lot_sqft).toLocaleString()} sq ft` : ''}</span> : null}
            {prop.service_status === 'cancelled' && (
              <span className="text-xs text-red-700 bg-red-50 border border-red-200 px-1.5 py-0.5 rounded font-medium" title={prop.service_cancel_reason || ''}>
                service cancelled{prop.service_cancelled_effective ? ` from ${prop.service_cancelled_effective}` : ''}
              </span>
            )}
          </div>
          <div className="flex gap-3 mt-1 flex-wrap">
            {prop.mowing_price != null && (
              <span className="flex items-center gap-1 text-xs text-green-700 font-semibold">
                <DollarSign className="h-3 w-3" />{prop.mowing_price.toFixed(2)}
              </span>
            )}
            {prop.estimated_minutes && (
              <span className="flex items-center gap-1 text-xs text-gray-500">
                <Clock className="h-3 w-3" />{prop.estimated_minutes}min
              </span>
            )}
            {prop.cutting_height && <span className="text-xs text-gray-500">Cut: {prop.cutting_height}</span>}
          </div>
          {prop.crew_notes && <p className="text-xs text-orange-600 mt-1 italic">{prop.crew_notes}</p>}
          {prop.gate_notes && <p className="text-xs text-blue-600 mt-0.5">Gate: {prop.gate_notes}</p>}
          <RouteDayHint prop={prop} />
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button onClick={onSchedule} title="Schedule job" className="p-1.5 text-gray-500 hover:text-brand-600 rounded">
            <Calendar className="h-3.5 w-3.5" />
          </button>
          {/* The site map: what gets done where, and the locks with their codes (components/SiteMapEditor). */}
          <Link to={`/site-map/${prop.id}`} title="Site map" className="p-1.5 text-gray-500 hover:text-brand-600 rounded">
            <MapIcon className="h-3.5 w-3.5" />
          </Link>
          <button onClick={onEdit} title="Edit property" className="p-1.5 text-gray-500 hover:text-brand-600 rounded">
            <Edit2 className="h-3.5 w-3.5" />
          </button>
          <button disabled={readOnly} onClick={onDelete} title="Remove property" className="p-1.5 text-gray-500 hover:text-red-500 rounded">
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 flex-shrink-0">
          <h3 className="text-base font-black uppercase tracking-tight text-gray-900">{title}</h3>
          <button onClick={onClose}><X className="h-5 w-5 text-gray-500 hover:text-gray-600" /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="mb-3 last:mb-0">
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      {children}
    </div>
  );
}

function ModalFooter({ onCancel, onSave, saving, label }: { onCancel: () => void; onSave: () => void; saving: boolean; label: string }) {
  const readOnly = useReadOnly();   // view-only login: grey out what can only ever be refused
  return (
    <div className="flex justify-end gap-3">
      <button onClick={onCancel} className="btn-secondary">Cancel</button>
      <button onClick={onSave} disabled={readOnly || (saving)} className="btn-primary">
        {saving ? 'Saving…' : label}
      </button>
    </div>
  );
}

/**
 * Their private app link, their referral code, and what they have earned.
 * The link is the login — text it once and they can bookmark it.
 */
/**
 * The texts, kept.
 *
 * Michael texts from his own phone so customers keep iMessage. A scanner on his
 * Mac copies every message here, matched by phone number. This only reads — a
 * reply from the app would come from a number the customer does not know.
 */
/**
 * Service mode (owner, Oct 20 2026): Regular / As needed / Off, with the turn-back-on date. As needed asks them
 * by text before each visit and books it only on a yes; Off takes the upcoming visits off the schedule and books
 * nothing until the date. services/serviceMode on the server.
 */
function CustomerServiceBlock({ customerId }: { customerId: number }) {
  const qc = useQueryClient();
  const readOnly = useReadOnly();
  const { data, isLoading } = useQuery(['customer-service-mode', customerId], () => api.get(`/as-needed/customers/${customerId}`).then(r => r.data));
  const [resumeOn, setResumeOn] = useState('');
  const [offOpen, setOffOpen] = useState(false);
  const refresh = () => { qc.invalidateQueries(['customer-service-mode', customerId]); qc.invalidateQueries('customers'); qc.invalidateQueries('attention'); };
  const save = useMutation((body: { mode: string; resume_on?: string | null }) => api.put(`/as-needed/customers/${customerId}`, body).then(r => r.data), {
    onSuccess: (d: any) => { toast.success(d?.message || 'Saved'); setOffOpen(false); refresh(); },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not change that'); },
  });
  const answer = useMutation(({ id, what }: { id: number; what: 'accept' | 'decline' }) => api.post(`/as-needed/offers/${id}/${what}`).then(r => r.data), {
    onSuccess: (d: any) => { toast.success(d?.message || 'Done'); refresh(); },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not do that'); },
  });
  if (isLoading || !data) return null;
  const mode: string = data.mode;
  const open = (data.offers || []).filter((o: any) => o.open);
  const choice = (key: string, label: string) => (
    <button key={key} type="button" disabled={readOnly || save.isLoading}
      onClick={() => { if (key === 'off') { setOffOpen(v => !v); return; } if (key !== mode) save.mutate({ mode: key }); }}
      className={`px-2.5 py-1 rounded-lg text-xs font-semibold border disabled:opacity-50 ${mode === key ? 'bg-brand-600 text-black border-brand-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}>
      {label}
    </button>
  );
  return (
    <div className="mt-3 border-t border-gray-100 pt-3">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs font-semibold text-gray-600">Service</span>
        {choice('regular', 'Regular')}
        {choice('as_needed', 'As needed')}
        {choice('off', mode === 'off' ? 'Off' : 'Turn off…')}
      </div>
      <p className="text-xs text-gray-600 mt-1.5">{data.words}</p>
      {mode === 'as_needed' && data.mowing_schedules === 0 && (
        <p className="text-xs text-amber-700 mt-1">No mowing schedule on this customer — add one and the asks start.</p>
      )}
      {mode === 'as_needed' && data.next_asks?.length > 0 && (
        <p className="text-xs text-gray-500 mt-1">
          Next ask: {data.next_asks.map((a: any) => `${a.date} (text goes ${a.text_on}${data.next_asks.length > 1 && a.address ? `, ${String(a.address).split(',')[0]}` : ''})`).join(' · ')}
        </p>
      )}
      {offOpen && (
        <div className="mt-2 flex items-center gap-2 flex-wrap text-xs">
          <label className="text-gray-600">Turn back on</label>
          <input type="date" value={resumeOn} onChange={e => setResumeOn(e.target.value)} className="border border-gray-300 rounded-md px-2 py-1 text-xs" />
          <button type="button" disabled={readOnly || save.isLoading} onClick={() => save.mutate({ mode: 'off', resume_on: resumeOn || null })}
            className="px-2.5 py-1 rounded-lg text-xs font-semibold text-white bg-gray-800 hover:bg-gray-900 disabled:opacity-50">
            {resumeOn ? `Off until ${resumeOn}` : 'Off until I say'}
          </button>
          <span className="text-gray-500">Upcoming visits come off the schedule; nothing is texted.</span>
        </div>
      )}
      {open.length > 0 && (
        <ul className="mt-2 space-y-1">
          {open.map((o: any) => (
            <li key={o.id} className="flex items-center gap-2 flex-wrap text-xs">
              <span className="text-gray-700">
                Asked about {o.day_words}{o.address ? ` at ${String(o.address).split(',')[0]}` : ''}{o.asked_at ? '' : o.no_text_reason ? ` — not texted: ${o.no_text_reason}` : ' — text pending'}
              </span>
              <button type="button" disabled={readOnly || answer.isLoading} onClick={() => answer.mutate({ id: o.id, what: 'accept' })}
                className="px-2 py-0.5 rounded-md text-[11px] font-semibold text-black bg-brand-600 hover:bg-brand-500 disabled:opacity-50">Put them on</button>
              <button type="button" disabled={readOnly || answer.isLoading} onClick={() => answer.mutate({ id: o.id, what: 'decline' })}
                className="px-2 py-0.5 rounded-md text-[11px] font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 disabled:opacity-50">Skip</button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Several numbers on one customer (owner, Oct 28 2026: "multiple number on a client and some can be for billing
 * some for updates"). The main number is the one on the card (Edit changes it); the others are added here, each
 * with the kinds of text it gets — the same kinds as Customers → Who gets which texts. A text goes to every
 * number ticked for its kind; a number with nothing ticked is on file for calls. Server: services/customerPhones
 * (GET/POST /customers/:id/phones, PUT …/phones/main, PUT|DELETE …/phones/:pid).
 */
type PhoneKind = { key: string; label: string; help: string };
type ExtraPhone = { id: number; phone: string; pretty: string; label: string | null; opted_out: boolean; gets: string[] };
type PhonesData = {
  kinds: PhoneKind[];
  main: { phone: string | null; pretty: string | null; other: string | null; opted_out: boolean; gets: string[] };
  extra: ExtraPhone[];
  nobody: string[];
};

function CustomerPhonesBlock({ customerId }: { customerId: number }) {
  const qc = useQueryClient();
  const readOnly = useReadOnly();
  const { data, isLoading } = useQuery<PhonesData>(['customer-phones', customerId], () => api.get(`/customers/${customerId}/phones`).then(r => r.data));
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<number | null>(null);
  const [form, setForm] = useState<{ phone: string; label: string; gets: string[] }>({ phone: '', label: '', gets: [] });
  const refresh = () => { qc.invalidateQueries(['customer-phones', customerId]); qc.invalidateQueries('text-prefs'); qc.invalidateQueries('customers'); };
  const done = (d: any) => { if (d?.message) toast.success(d.message); setAdding(false); setEditing(null); refresh(); };
  const fail = (e: any) => { toast.error(e?.response?.data?.message || 'Could not save that'); };
  const setMain = useMutation((gets: string[]) => api.put(`/customers/${customerId}/phones/main`, { gets }).then(r => r.data), { onSuccess: done, onError: fail });
  const setExtra = useMutation(({ id, body }: { id: number; body: any }) => api.put(`/customers/${customerId}/phones/${id}`, body).then(r => r.data), { onSuccess: done, onError: fail });
  const add = useMutation((body: any) => api.post(`/customers/${customerId}/phones`, body).then(r => r.data), { onSuccess: done, onError: fail });
  const remove = useMutation((id: number) => api.delete(`/customers/${customerId}/phones/${id}`).then(r => r.data), { onSuccess: done, onError: fail });
  if (isLoading || !data) return null;
  const busy = readOnly || setMain.isLoading || setExtra.isLoading || add.isLoading || remove.isLoading;
  const kindName = (k: string) => data.kinds.find(x => x.key === k)?.label || k;
  const toggle = (list: string[], k: string) => (list.includes(k) ? list.filter(x => x !== k) : [...list, k]);
  const chips = (gets: string[], onChange: (next: string[]) => void) => (
    <div className="flex flex-wrap gap-1">
      {data.kinds.map(k => {
        const on = gets.includes(k.key);
        return (
          <button key={k.key} type="button" disabled={busy} title={k.help} onClick={() => onChange(toggle(gets, k.key))}
            className={`px-2 py-0.5 rounded-md text-[11px] font-semibold border disabled:opacity-50 ${on ? 'bg-brand-600 text-black border-brand-600' : 'bg-white text-gray-500 border-gray-300 hover:bg-gray-50'}`}>
            {on ? '✓ ' : ''}{k.label}
          </button>
        );
      })}
    </div>
  );
  const startEdit = (e: ExtraPhone) => { setEditing(e.id); setAdding(false); setForm({ phone: e.phone, label: e.label || '', gets: e.gets }); };
  const startAdd = () => { setAdding(true); setEditing(null); setForm({ phone: '', label: '', gets: [] }); };
  const formBox = (onSave: () => void, saving: boolean, label: string) => (
    <div className="mt-2 rounded-lg border border-gray-200 bg-gray-50 p-2 space-y-2 text-xs">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="(512) 555-0100" className="input text-xs" inputMode="tel" />
        <input value={form.label} onChange={e => setForm(f => ({ ...f, label: e.target.value }))} placeholder="Who is this? e.g. Mark — husband" className="input text-xs" maxLength={60} />
      </div>
      <div>
        <p className="text-gray-600 mb-1">Which texts go to this number</p>
        {chips(form.gets, gets => setForm(f => ({ ...f, gets })))}
      </div>
      <div className="flex gap-2">
        <button type="button" disabled={busy || !form.phone.trim()} onClick={onSave}
          className="px-2.5 py-1 rounded-lg text-xs font-semibold text-black bg-brand-600 hover:bg-brand-500 disabled:opacity-50">{saving ? 'Saving…' : label}</button>
        <button type="button" onClick={() => { setAdding(false); setEditing(null); }}
          className="px-2.5 py-1 rounded-lg text-xs font-semibold text-gray-700 bg-white border border-gray-300 hover:bg-gray-50">Cancel</button>
      </div>
    </div>
  );
  return (
    <div className="mt-3 border-t border-gray-100 pt-3">
      <p className="text-xs font-semibold text-gray-700">Who gets which texts and emails</p>
      <div className="mt-1.5 flex items-center justify-between gap-2 flex-wrap">
        <span className="text-xs font-semibold text-gray-600">Phone numbers</span>
        {!adding && (
          <button type="button" disabled={busy} onClick={startAdd} className="text-xs font-semibold text-brand-700 hover:underline disabled:opacity-50">+ Add a number</button>
        )}
      </div>
      <ul className="mt-1.5 space-y-2">
        <li className="text-xs">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-gray-900">{data.main.pretty || 'No main number'}</span>
            <span className="text-gray-500">main{data.main.opted_out ? ' · replied STOP' : ''}</span>
            {data.main.other && <span className="text-gray-400">· also {data.main.other} on file</span>}
            {!data.main.phone && <span className="text-gray-500">— add one with Edit</span>}
          </div>
          {data.main.phone && <div className="mt-1">{chips(data.main.gets, gets => setMain.mutate(gets))}</div>}
        </li>
        {data.extra.map(e => (
          <li key={e.id} className="text-xs">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-gray-900">{e.pretty}</span>
              <span className="text-gray-500">{e.label || 'other number'}{e.opted_out ? ' · replied STOP' : ''}</span>
              <Link to={`/texts/${String(e.phone).replace(/\D/g, '').slice(-10)}`} className="text-brand-700 hover:underline">Text</Link>
              {editing !== e.id && (
                <>
                  <button type="button" disabled={busy} onClick={() => startEdit(e)} className="text-gray-600 hover:underline disabled:opacity-50">Edit</button>
                  <button type="button" disabled={busy} onClick={() => { if (window.confirm(`Take ${e.pretty} off this customer?`)) remove.mutate(e.id); }}
                    className="text-red-600 hover:underline disabled:opacity-50">Remove</button>
                </>
              )}
            </div>
            {editing === e.id
              ? formBox(() => setExtra.mutate({ id: e.id, body: { phone: form.phone, label: form.label, gets: form.gets } }), setExtra.isLoading, 'Save')
              : <div className="mt-1">{chips(e.gets, gets => setExtra.mutate({ id: e.id, body: { gets } }))}</div>}
          </li>
        ))}
      </ul>
      {adding && formBox(() => add.mutate({ phone: form.phone, label: form.label, gets: form.gets }), add.isLoading, 'Add number')}
      <p className="text-[11px] text-gray-500 mt-1.5">
        A text goes to every number ticked for its kind. A number with nothing ticked is on file for calls only.
        {data.nobody.length > 0 && data.nobody.length < data.kinds.length && <span className="text-amber-700"> Nobody gets: {data.nobody.map(kindName).join(', ')}.</span>}
        {data.nobody.length === data.kinds.length && <span className="text-amber-700"> No texts go to this customer.</span>}
      </p>
      <CustomerEmailsList customerId={customerId} />
    </div>
  );
}

/**
 * The emails, under the numbers (owner, Nov 2 2026: "on the phone number thing … add emails too"). Every address
 * in one list, the main one among them — the one the card shows and a sign-in code goes to — each with the kinds
 * of email it gets: bills, bids, the visit report, a job update. Server: services/customerEmails
 * (GET/POST /customers/:id/emails, PUT …/emails/:eid, PUT …/emails/:eid/main, DELETE …/emails/:eid).
 */
type EmailRow = { id: number; email: string; label: string | null; main: boolean; gets: string[] };
type EmailsData = { kinds: PhoneKind[]; rows: EmailRow[]; contacts: { name: string | null; email: string }[]; nobody: string[] };

function CustomerEmailsList({ customerId }: { customerId: number }) {
  const qc = useQueryClient();
  const readOnly = useReadOnly();
  const { data, isLoading } = useQuery<EmailsData>(['customer-emails', customerId], () => api.get(`/customers/${customerId}/emails`).then(r => r.data));
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<number | null>(null);
  const [form, setForm] = useState<{ email: string; label: string; gets: string[] }>({ email: '', label: '', gets: [] });
  // The main email is customers.email too, so the card and the list refresh with it.
  const refresh = () => { qc.invalidateQueries(['customer-emails', customerId]); qc.invalidateQueries('customers'); qc.invalidateQueries(['customer', customerId]); };
  const done = (d: any) => { if (d?.message) toast.success(d.message); setAdding(false); setEditing(null); refresh(); };
  const fail = (e: any) => { toast.error(e?.response?.data?.message || 'Could not save that'); };
  const save = useMutation(({ id, body }: { id: number; body: any }) => api.put(`/customers/${customerId}/emails/${id}`, body).then(r => r.data), { onSuccess: done, onError: fail });
  const makeMain = useMutation((id: number) => api.put(`/customers/${customerId}/emails/${id}/main`).then(r => r.data), { onSuccess: done, onError: fail });
  const add = useMutation((body: any) => api.post(`/customers/${customerId}/emails`, body).then(r => r.data), { onSuccess: done, onError: fail });
  const remove = useMutation((id: number) => api.delete(`/customers/${customerId}/emails/${id}`).then(r => r.data), { onSuccess: done, onError: fail });
  if (isLoading || !data) return null;
  const busy = readOnly || save.isLoading || makeMain.isLoading || add.isLoading || remove.isLoading;
  const kindName = (k: string) => data.kinds.find(x => x.key === k)?.label || k;
  const toggle = (list: string[], k: string) => (list.includes(k) ? list.filter(x => x !== k) : [...list, k]);
  const looksLikeEmail = (v: string) => /^[^@\s,;]+@[^@\s,;]+\.[^@\s,;]+$/.test(v.trim());
  const chips = (gets: string[], onChange: (next: string[]) => void) => (
    <div className="flex flex-wrap gap-1">
      {data.kinds.map(k => {
        const on = gets.includes(k.key);
        return (
          <button key={k.key} type="button" disabled={busy} title={k.help} onClick={() => onChange(toggle(gets, k.key))}
            className={`px-2 py-0.5 rounded-md text-[11px] font-semibold border disabled:opacity-50 ${on ? 'bg-brand-600 text-black border-brand-600' : 'bg-white text-gray-500 border-gray-300 hover:bg-gray-50'}`}>
            {on ? '✓ ' : ''}{k.label}
          </button>
        );
      })}
    </div>
  );
  const startEdit = (e: EmailRow) => { setEditing(e.id); setAdding(false); setForm({ email: e.email, label: e.label || '', gets: e.gets }); };
  const startAdd = () => { setAdding(true); setEditing(null); setForm({ email: '', label: '', gets: data.rows.length ? [] : data.kinds.map(k => k.key) }); };
  const formBox = (onSave: () => void, saving: boolean, label: string) => (
    <div className="mt-2 rounded-lg border border-gray-200 bg-gray-50 p-2 space-y-2 text-xs">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <input value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="name@example.com" className="input text-xs" inputMode="email" type="email" maxLength={200} />
        <input value={form.label} onChange={e => setForm(f => ({ ...f, label: e.target.value }))} placeholder="Who is this? e.g. AP, or Mark — husband" className="input text-xs" maxLength={60} />
      </div>
      {form.email.trim() && !looksLikeEmail(form.email) && <p className="text-amber-700">That does not look like an email address.</p>}
      <div>
        <p className="text-gray-600 mb-1">Which emails go to this address</p>
        {chips(form.gets, gets => setForm(f => ({ ...f, gets })))}
      </div>
      <div className="flex gap-2">
        <button type="button" disabled={busy || !looksLikeEmail(form.email)} onClick={onSave}
          className="px-2.5 py-1 rounded-lg text-xs font-semibold text-black bg-brand-600 hover:bg-brand-500 disabled:opacity-50">{saving ? 'Saving…' : label}</button>
        <button type="button" onClick={() => { setAdding(false); setEditing(null); }}
          className="px-2.5 py-1 rounded-lg text-xs font-semibold text-gray-700 bg-white border border-gray-300 hover:bg-gray-50">Cancel</button>
      </div>
    </div>
  );
  return (
    <div className="mt-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <span className="text-xs font-semibold text-gray-600">Emails</span>
        {!adding && (
          <button type="button" disabled={busy} onClick={startAdd} className="text-xs font-semibold text-brand-700 hover:underline disabled:opacity-50">+ Add an email</button>
        )}
      </div>
      <ul className="mt-1.5 space-y-2">
        {data.rows.length === 0 && !adding && <li className="text-xs text-gray-500">No email on file.</li>}
        {data.rows.map(e => (
          <li key={e.id} className="text-xs">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-gray-900 break-all">{e.email}</span>
              <span className="text-gray-500">{e.main ? 'main' : (e.label || 'other email')}{e.main && e.label ? ` · ${e.label}` : ''}</span>
              {editing !== e.id && (
                <>
                  <button type="button" disabled={busy} onClick={() => startEdit(e)} className="text-gray-600 hover:underline disabled:opacity-50">Edit</button>
                  {!e.main && (
                    <button type="button" disabled={busy} onClick={() => makeMain.mutate(e.id)} className="text-gray-600 hover:underline disabled:opacity-50" title="The one the card shows and a sign-in code goes to">Make main</button>
                  )}
                  <button type="button" disabled={busy} onClick={() => { if (window.confirm(`Take ${e.email} off this customer?`)) remove.mutate(e.id); }}
                    className="text-red-600 hover:underline disabled:opacity-50">Remove</button>
                </>
              )}
            </div>
            {editing === e.id
              ? formBox(() => save.mutate({ id: e.id, body: { email: form.email, label: form.label, gets: form.gets } }), save.isLoading, 'Save')
              : <div className="mt-1">{chips(e.gets, gets => save.mutate({ id: e.id, body: { gets } }))}</div>}
          </li>
        ))}
        {data.contacts.map((c, i) => (
          <li key={`c${i}`} className="text-xs">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-gray-900 break-all">{c.email}</span>
              <span className="text-gray-500">{c.name ? `AP · ${c.name}` : 'AP contact'} · gets bills and bids — set on the Commercial account's contacts</span>
            </div>
          </li>
        ))}
      </ul>
      {adding && formBox(() => add.mutate({ email: form.email, label: form.label, gets: form.gets }), add.isLoading, 'Add email')}
      <p className="text-[11px] text-gray-500 mt-1.5">
        An email goes to every address ticked for its kind; the main one is what the card shows and where a sign-in code goes.
        {data.nobody.length > 0 && data.nobody.length < data.kinds.length && <span className="text-amber-700"> No email for: {data.nobody.map(kindName).join(', ')}.</span>}
        {data.rows.length > 0 && data.nobody.length === data.kinds.length && <span className="text-amber-700"> No emails go to this customer.</span>}
      </p>
    </div>
  );
}

function CustomerTextsBlock({ customerId }: { customerId: number }) {
  const [open, setOpen] = useState(false);
  const { data, isLoading } = useQuery(['customer-texts', customerId],
    () => api.get(`/customers/${customerId}/messages`).then(r => r.data.messages as any[]),
    { enabled: open });
  const msgs = data || [];
  return (
    <div className="mt-3 border-t border-gray-100 pt-3">
      <button onClick={() => setOpen(v => !v)} className="text-xs font-semibold text-gray-600 hover:text-gray-900">
        {open ? '▾' : '▸'} Texts
      </button>
      {open && isLoading && <p className="text-xs text-gray-500 mt-2">Loading…</p>}
      {open && !isLoading && msgs.length === 0 && (
        <p className="text-xs text-gray-500 mt-2">
          No texts kept yet. They arrive from your phone each morning; a customer with no phone number on file never matches.
        </p>
      )}
      {open && msgs.length > 0 && (
        <ul className="mt-2 space-y-1.5 max-h-72 overflow-y-auto pr-1">
          {msgs.map((m: any) => (
            <li key={m.id} className={`flex ${m.direction === 'out' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[80%] rounded-2xl px-3 py-1.5 text-sm ${m.direction === 'out' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-900'}`}>
                <p className="whitespace-pre-wrap break-words">{m.body}</p>
                <p className={`text-[10px] mt-0.5 ${m.direction === 'out' ? 'text-blue-100' : 'text-gray-500'}`}>
                  {fmtTs(m.sent_at, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                </p>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * The client groups this customer is in — one line, the same add/remove as
 * the Client map's pin popup. Owner, Oct 3 2026: "for the client map I want
 * to be able to make groups … some of them are just landscaping clients."
 * Groups are made on the map (Groups…); here he only puts a person in or out.
 */
function CustomerGroupsLine({ customerId, customerName }: { customerId: number; customerName?: string }) {
  const qc = useQueryClient();
  const readOnly = useReadOnly();
  const { data } = useQuery(['customer-groups', customerId],
    () => api.get('/client-groups', { params: { customer_id: customerId } }).then(r => r.data as { groups: any[]; in: number[] }),
    { staleTime: 60_000 });
  const groups: any[] = data?.groups || [];
  const mine = new Set<number>(data?.in || []);
  const change = useMutation(
    (v: { group: any; add: boolean }) => api.put(`/client-groups/${v.group.id}/members`, v.add ? { add: [customerId] } : { remove: [customerId] }),
    {
      onSuccess: (_r, v) => {
        toast.success(v.add ? `${customerName || 'They'} ${customerName ? 'is' : 'are'} in ${v.group.name} now` : `${customerName || 'They'} ${customerName ? 'is' : 'are'} out of ${v.group.name}`);
        qc.invalidateQueries(['customer-groups', customerId]); qc.invalidateQueries('clients-map');
      },
      onError: (e: any) => { toast.error(e.response?.data?.message || 'Could not change the group'); },
    });
  if (!groups.length) return null;   // no groups made yet — nothing to say here; they are made on the Client map
  const inGroups = groups.filter(g => mine.has(g.id));
  const notIn = groups.filter(g => !mine.has(g.id));
  return (
    <div className="mt-3 border-t border-gray-100 pt-3 flex flex-wrap items-center gap-1.5 text-xs">
      <span className="font-semibold text-gray-600">Groups:</span>
      {inGroups.length === 0 && <span className="text-gray-500">none</span>}
      {inGroups.map(g => (
        <span key={g.id} className="inline-flex items-center gap-1 rounded-full border border-gray-300 px-2 py-0.5 text-gray-800">
          <span className="inline-block h-2 w-2 rounded-full" style={{ background: g.color || '#9ca3af' }} />{g.name}
          <button type="button" disabled={readOnly || change.isLoading} onClick={() => change.mutate({ group: g, add: false })} title={`Take them out of ${g.name}`} className="text-gray-400 hover:text-gray-700 disabled:opacity-50">×</button>
        </span>
      ))}
      {notIn.length > 0 && (
        <select value="" disabled={readOnly || change.isLoading} onChange={e => { const g = groups.find(x => String(x.id) === e.target.value); if (g) change.mutate({ group: g, add: true }); }} className="border border-gray-300 rounded px-1 py-0.5 bg-white text-xs" title="Put this customer in a group">
          <option value="">Add to group ▾</option>
          {notIn.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
        </select>
      )}
      <Link to="/map" className="text-gray-500 underline ml-1">Groups live on the Client map</Link>
    </div>
  );
}

/**
 * Things this customer has mentioned wanting.
 *
 * "The bed out front could use a refresh in spring" — said once, in passing.
 * Noted with a month, it comes back up in Needs Attention when that month comes.
 */
function CustomerInterestsBlock({ customerId, customerName }: { customerId: number; customerName?: string }) {
  // Reach-outs, future work and "they wanted" notes, each with a date that
  // brings it back on My Day and in Needs Attention (services/reminders).
  const [open, setOpen] = useState(true);
  const [msg, setMsg] = useState<string | null>(null);
  const { data, refetch } = useQuery(['customer-reminders', customerId],
    () => api.get(`/reminders/customer/${customerId}`).then(r => r.data.reminders as any[]),
    { enabled: open });
  const rows = data || [];
  const openRows = rows.filter((r: any) => r.status === 'open');
  const doneRows = rows.filter((r: any) => r.status !== 'open');
  async function act(id: number, what: 'done' | 'drop' | 'reopen' | 'snooze') {
    try {
      const r = what === 'snooze' ? await api.post(`/reminders/${id}/snooze`, { when: 'week' }) : await api.post(`/reminders/${id}/${what}`);
      setMsg(r.data?.message || null); refetch();
    } catch { setMsg('Could not update that'); }
  }
  return (
    <div className="mt-3 border-t border-gray-100 pt-3">
      <button onClick={() => setOpen(v => !v)} className="text-xs font-semibold text-gray-600 hover:text-gray-900">
        {open ? '▾' : '▸'} Future work & reminders{openRows.length ? ` (${openRows.length})` : ''}
      </button>
      {open && (
        <div className="mt-2 space-y-2">
          {openRows.length > 0 && (
            <ul className="space-y-1">
              {openRows.map((r: any) => (
                <li key={r.id} className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="text-[10px] uppercase tracking-wide text-gray-500 w-20">{r.kind_word}</span>
                  {(r.photo_urls?.length ? r.photo_urls : r.photo_url ? [r.photo_url] : []).map((u: string, i: number) => (
                    <a key={i} href={`${SERVER_ORIGIN}${u}`} target="_blank" rel="noreferrer"><img src={`${SERVER_ORIGIN}${u}`} alt="" className="h-10 w-10 rounded-lg object-cover border border-gray-200" /></a>
                  ))}
                  <span className="text-gray-900">{r.note || (r.kind === 'reach_out' ? 'reach out' : '')}</span>
                  <span className={`text-[11px] ${r.overdue ? 'text-amber-700' : 'text-gray-500'}`}>{r.due_words}</span>
                  {Number(r.est_value) > 0 && <span className="text-[11px] text-gray-500">~${Number(r.est_value).toFixed(0)}</span>}
                  <span className="ml-auto flex gap-2">
                    <button onClick={() => act(r.id, 'done')} className="text-[11px] text-green-700 font-medium">done</button>
                    <button onClick={() => act(r.id, 'snooze')} className="text-[11px] text-gray-500">+1 wk</button>
                    <button onClick={() => act(r.id, 'drop')} className="text-[11px] text-gray-500">drop</button>
                  </span>
                </li>
              ))}
            </ul>
          )}
          <ReminderAdd customerId={customerId} customerName={customerName} compact onAdded={() => refetch()} />
          {doneRows.length > 0 && (
            <details className="text-xs text-gray-500">
              <summary className="cursor-pointer">{doneRows.length} closed</summary>
              <ul className="mt-1 space-y-0.5">
                {doneRows.map((r: any) => (
                  <li key={r.id} className="flex items-center gap-2"><span className="line-through">{r.title}</span><span>{r.status}</span>
                    <button onClick={() => act(r.id, 'reopen')} className="underline">reopen</button></li>
                ))}
              </ul>
            </details>
          )}
          {msg && <p className="text-xs text-gray-500">{msg}</p>}
        </div>
      )}
    </div>
  );
}

/**
 * To-dos for this customer at one of their addresses — "drop the sample bag at 415 Derby Ln" (owner, Oct 26
 * 2026; services/todos). Open ones first; done from here is done on My Day, the Calendar and the Field page too.
 * "To-do here" opens the shared sheet with the client filled in and their yards to pick from.
 */
function CustomerTodosBlock({ customerId, customerName, properties }: { customerId: number; customerName: string; properties: Property[] }) {
  const readOnly = useReadOnly();
  const [open, setOpen] = useState(true);
  const [sheet, setSheet] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const { data, refetch } = useQuery(['customer-todos', customerId],
    () => api.get(`/todos/customer/${customerId}`).then(r => r.data.todos as any[]), { enabled: open });
  const rows = data || [];
  const openRows = rows.filter((t: any) => !t.done);
  const doneRows = rows.filter((t: any) => t.done);
  async function act(id: number, what: 'done' | 'reopen') {
    try { const r = await api.post(`/todos/${id}/${what}`); setMsg(r.data?.message || null); refetch(); }
    catch (e: any) { setMsg(e?.response?.data?.message || 'Could not update that'); }
  }
  return (
    <div className="mt-3 border-t border-gray-100 pt-3">
      <div className="flex items-center justify-between">
        <button onClick={() => setOpen(v => !v)} className="text-xs font-semibold text-gray-600 hover:text-gray-900">
          {open ? '▾' : '▸'} To-dos{openRows.length ? ` (${openRows.length})` : ''}
        </button>
        {!readOnly && <button onClick={() => setSheet(true)} className="text-xs font-semibold text-brand-700">☐ To-do here</button>}
      </div>
      {open && (
        <div className="mt-2 space-y-2">
          {openRows.length === 0 && doneRows.length === 0 && <p className="text-xs text-gray-500">Nothing to do at their place.</p>}
          {openRows.length > 0 && (
            <ul className="space-y-1">
              {openRows.map((t: any) => (
                <li key={t.id} className="flex flex-wrap items-center gap-2 text-sm">
                  <Link to={t.href} className="text-gray-900 hover:underline">{t.what}</Link>
                  <span className={`text-[11px] ${t.overdue ? 'text-amber-700' : 'text-gray-500'}`}>{t.day_words}{t.time_words ? ` ${t.time_words}` : ''}</span>
                  {t.street && <span className="text-[11px] text-gray-500">{t.street}</span>}
                  <span className="text-[11px] text-gray-500">{t.for_words}</span>
                  {!readOnly && <button onClick={() => act(t.id, 'done')} className="ml-auto text-[11px] text-green-700 font-medium">done</button>}
                </li>
              ))}
            </ul>
          )}
          {doneRows.length > 0 && (
            <details className="text-xs text-gray-500">
              <summary className="cursor-pointer">{doneRows.length} done</summary>
              <ul className="mt-1 space-y-0.5">
                {doneRows.map((t: any) => (
                  <li key={t.id} className="flex items-center gap-2">
                    <span className="line-through">{t.what}</span>
                    <span>{t.day_words}{t.done_by ? ` · ${String(t.done_by).split(' ')[0]}` : ''}</span>
                    {!readOnly && <button onClick={() => act(t.id, 'reopen')} className="underline">reopen</button>}
                  </li>
                ))}
              </ul>
            </details>
          )}
          {msg && <p className="text-xs text-gray-500">{msg}</p>}
        </div>
      )}
      <TodoSheet open={sheet} onClose={() => setSheet(false)} customer={{ id: customerId, name: customerName }} properties={properties}
        title={`To-do for ${customerName}`} onAdded={() => { setSheet(false); refetch(); }} />
    </div>
  );
}

/**
 * Which route day suits this address — the same answer the bid gives (the
 * drive each route running today would add; with no map pin, the days this
 * town's yards are mowed now). A yard already on the schedule is told its day.
 * Owner, Oct 5 2026: "new route days based on new route days".
 */
function RouteDayHint({ prop }: { prop: Property }) {
  const [hint, setHint] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  async function ask() {
    setBusy(true);
    try {
      const r = await api.get('/routes/suggest-for', { params: { property_id: prop.id } });
      setHint(r.data?.message || 'No suggestion');
    } catch (e: any) { setHint(e.response?.data?.message || 'Could not work that out'); }
    finally { setBusy(false); }
  }
  return hint
    ? <p className="text-xs text-brand-700 mt-1">Route day: {hint}</p>
    : <button onClick={ask} disabled={busy} className="text-xs text-brand-600 hover:underline mt-1">
        {busy ? 'Working it out…' : 'Which route day suits this address?'}
      </button>;
}

function ChangePlanLink({ programId, customerId }: { programId: number; customerId: number }) {
  const [open, setOpen] = useState(false);
  if (!open) return <button type="button" onClick={() => setOpen(true)} className="mt-1 text-xs font-semibold text-emerald-800 underline underline-offset-2">Change what is in the plan (extras, months, price)</button>;
  return <CustomPlanBuilder programId={programId} customerId={customerId} onDone={() => setOpen(false)} onCancel={() => setOpen(false)} />;
}

// ── Lawn plan options for this customer ─────────────────────────────────────
// Owner, Sep 19 2026: "find her customer page, find her address, do the lawn
// size, and then find her treatment plan options." One tap: the yard, its lawn
// size and where that number came from, the three plans priced for it (highest
// first) with what each includes, and the link to text them.
function PlanOptionsBlock({ customerId }: { customerId: number }) {
  const [open, setOpen] = useState(false);
  const { data, isLoading } = useQuery(['customer-plan-options', customerId], () => api.get(`/customers/${customerId}/plan-options`).then(r => r.data), { enabled: open });
  return (
    <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2.5">
      <button type="button" onClick={() => setOpen(v => !v)} className="w-full flex items-center justify-between text-xs font-bold uppercase tracking-wide text-emerald-800">
        <span>Lawn plan options</span><span className="font-normal normal-case text-emerald-700">{open ? 'close' : 'show prices for this yard'}</span>
      </button>
      {open && isLoading && <p className="text-xs text-gray-500 mt-2">Working it out…</p>}
      {open && data && (
        <div className="mt-2 space-y-3">
          {data.yards.length === 0 && <p className="text-xs text-gray-600">No property on this customer yet — add the address and the lawn gets sized.</p>}
          {data.yards.map((y: any) => (
            <div key={y.property_id} className="rounded-lg bg-white border border-emerald-200 p-2.5">
              <p className="text-sm font-semibold text-gray-900">{y.address || 'Yard'}</p>
              <p className="text-xs text-gray-600">
                {y.turf_known ? <>Lawn <b>{Number(y.turf_sqft).toLocaleString()} sq ft</b> ({y.turf_from}{y.turf_flag ? ' — check it on a visit' : ''})</> : <>Lawn not measured — priced at {Number(y.priced_at_sqft).toLocaleString()} sq ft until it is</>}
                {y.satellite_sqft ? <> · satellite says {Number(y.satellite_sqft).toLocaleString()}</> : null}
                {y.grass ? <> · {y.grass}</> : <> · grass not set</>} · {y.mowed_by_us ? 'we mow it (mowing-customer price)' : 'we do not mow it'}
              </p>
              {y.on_plan && <p className="text-xs text-emerald-800 mt-0.5">Already on {y.on_plan.name}{y.on_plan.monthly ? ` at ${y.on_plan.monthly}/mo` : ''}.</p>}
              {/* A running plan is changed on the same builder that makes a custom one: an extra added or dropped, a month moved (owner, Oct 22 2026). */}
              {y.on_plan?.id && <ChangePlanLink programId={y.on_plan.id} customerId={customerId} />}
              {/* A bid they sign: the three plans priced for this yard, the whole year, and they are enrolled on signature. */}
              {!y.on_plan && <PlanBidButton propertyId={y.property_id} customerId={customerId} phone={data.customer?.phone} />}
              {/* Already said yes in person: start them now, at the price he quoted. */}
              {!y.on_plan && <StartPlanForm yard={y} customerId={customerId} />}
              <div className="mt-1.5 space-y-1.5">
                {y.plans.map((pl: any) => (
                  <div key={pl.plan} className="text-xs text-gray-800">
                    <p><b>{pl.name}</b> — <b>${pl.monthly}/mo</b> <span className="text-gray-500">(${pl.yearly}/yr · {pl.visits} applications)</span>{pl.recommended ? <span className="ml-1 text-emerald-700">recommended</span> : null}</p>
                    {pl.includes.length > 0 && <p className="text-gray-600">{pl.includes.join(' · ')}</p>}
                  </div>
                ))}
              </div>
            </div>
          ))}
          <p className="text-[11px] text-gray-500">Send the three plans as a bid — they pick one and sign. Nothing is sent until you send it.</p>
        </div>
      )}
    </div>
  );
}

function CustomerAppBlock({ customerId }: { customerId: number }) {
  const readOnly = useReadOnly();   // view-only login: grey out what can only ever be refused
  const qc = useQueryClient();
  const [copied, setCopied] = useState(false);
  const { data } = useQuery(['customer-portal', customerId], () => api.get(`/customers/${customerId}/portal`).then(r => r.data));

  const setMilitary = useMutation((status: string) => api.post(`/customers/${customerId}/military`, { status }), {
    onSuccess: (r: any) => { toast.success(r.data.message); qc.invalidateQueries(['customer-portal', customerId]); },
    onError: () => { toast.error('Could not update that'); },
  });
  const roll = useMutation(() => api.post(`/customers/${customerId}/portal/roll`), {
    onSuccess: (r: any) => { toast.success(r.data.message); qc.invalidateQueries(['customer-portal', customerId]); },
  });
  const [seeing, setSeeing] = useState(false);

  if (!data) return null;
  const copy = (text: string) => { navigator.clipboard?.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000); };
  const smsText = `Here's your Keep Austin Mowed page — your schedule, invoices and rewards: ${data.url}`;

  return (
    <div className="mt-3 rounded-lg border border-brand-200 bg-brand-50 px-3 py-2.5">
      <p className="text-xs font-bold uppercase tracking-wide text-brand-700 mb-1.5">Their app &amp; rewards</p>

      {seeing && <CustomerView url={data.url} title="Their page" onClose={() => setSeeing(false)} />}

      <div className="flex flex-wrap items-center gap-2">
        <code className="text-[11px] bg-white border border-brand-200 rounded px-2 py-1 max-w-full truncate">{data.url}</code>
        <button onClick={() => copy(smsText)} className="press text-xs px-2 py-1 rounded bg-brand-600 text-black font-semibold">
          {copied ? 'Copied' : 'Copy text to send'}
        </button>
        <button onClick={() => setSeeing(true)} className="text-xs px-2 py-1 rounded border border-brand-300 text-brand-700 font-semibold">
          See their page
        </button>
        <button disabled={readOnly} onClick={() => { if (window.confirm('Make a new link? The one they already have will stop working.')) roll.mutate(); }}
          className="text-xs text-gray-500 underline">New link</button>
        {data.last_seen && <span className="text-[11px] text-gray-500">opened {String(data.last_seen).slice(0, 10)}</span>}
      </div>

      {/* The free lawn walkthrough (owner, Oct 20 2026): what is booked, book one for them, their link. */}
      <LawnWalkthroughCard customerId={customerId} />

      <div className="mt-2 flex flex-wrap items-center gap-3 text-xs">
        {data.referral_code && <span>Code <b className="tracking-wider">{data.referral_code}</b></span>}
        {data.referrals?.length > 0 && <span className="text-gray-600">referred {data.referrals.length}</span>}
        <span className="flex items-center gap-1">
          Military:
          <select disabled={readOnly} value={data.military_status} onChange={e => setMilitary.mutate(e.target.value)}
            className="border border-gray-300 rounded px-1.5 py-0.5 text-xs bg-white">
            <option value="none">not claimed</option>
            <option value="pending">claimed — verify</option>
            <option value="verified">verified · {Number(data.military_pct) || 10}% off every bill</option>
          </select>
        </span>
      </div>

      {data.credits?.length > 0 && (
        <p className="mt-2 text-xs text-green-800">
          Owed to them: {data.credits.map((c: any) => `${c.kind === 'percent' ? c.amount + '%' : '$' + c.amount} off ${c.applies_to}`).join(' · ')}
        </p>
      )}
      {data.military_status === 'pending' && (
        <p className="mt-1 text-xs text-amber-800">They say they served{data.military_note ? ` — "${data.military_note}"` : ''}. Verify before the discount applies.</p>
      )}
      {data.military_status === 'verified' && (
        <p className="mt-1 text-xs text-green-800">Military discount on: every draft bill from now carries "Military discount ({Number(data.military_pct) || 10}%)". Bills already sent are not changed. The percent is in Settings → Billing.</p>
      )}
    </div>
  );
}

function LotSizeRow({ propForm, setPropForm, prop }: { propForm: any; setPropForm: any; prop?: Property }) {
  const readOnly = useReadOnly();   // view-only login: grey out what can only ever be refused
  const sqft = Number(propForm.lot_sqft) || 0;
  const { data } = useQuery(['price-for', sqft], () => api.get('/quotes/price-for', { params: { sqft } }).then(r => r.data), { enabled: sqft > 0, staleTime: 60_000 });
  const qc = useQueryClient();
  // Turf from the county map (services/turf) — sized the moment the address went in; this re-asks the map.
  const [turf, setTurf] = useState<any>(null);
  const size = useMutation(() => api.post(`/properties/${prop!.id}/turf`, {}).then(r => r.data), {
    onSuccess: (d: any) => {
      setTurf(d);
      setPropForm((f: any) => ({ ...f, lot_sqft: f.lot_sqft || d.lot_sqft || f.lot_sqft, treatable_sqft: d.source === 'typed' ? f.treatable_sqft : (d.turf_sqft ?? '') }));
      qc.invalidateQueries('customers');
    },
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Could not size the turf'); },
  });
  const typed = prop?.turf_source === 'typed' && !!prop?.treatable_sqft;
  const stored = prop && !turf ? {
    known: !!prop.turf_assessed_at || typed, sure: typed || (!!prop.turf_assessed_at && !prop.turf_flag && prop.turf_source === 'county map'), band: typed ? null : prop.turf_band, flag: typed ? null : prop.turf_flag,
    label: prop.treatable_sqft && prop.turf_source === 'typed' ? `Turf ${Number(prop.treatable_sqft).toLocaleString()} sq ft — typed by hand`
      : prop.treatable_sqft ? `Turf about ${Number(prop.treatable_sqft).toLocaleString()} sq ft (county map${prop.lot_sqft ? `, lot ${Number(prop.lot_sqft).toLocaleString()}` : ''})${prop.turf_flag ? ' — check it on the visit' : ''}`
      : prop.turf_assessed_at ? `${prop.lot_sqft ? `Lot ${Number(prop.lot_sqft).toLocaleString()} sq ft — ` : ''}turf not sized from the map — measure on the visit` : 'Not sized from the county map yet',
  } : null;
  return (
    <div className="grid grid-cols-2 gap-3 items-end">
      {prop && (
        <div className="col-span-2 flex items-start gap-2">
          <div className="flex-1"><TurfNote turf={turf || stored} className="mt-0" /></div>
          <button type="button" onClick={() => size.mutate()} disabled={readOnly || (size.isLoading)} className="text-xs px-2.5 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 whitespace-nowrap disabled:opacity-50">
            {size.isLoading ? 'Asking the map…' : 'Size from county map'}
          </button>
        </div>
      )}
      {prop && <SatelliteTurf prop={prop} onUse={(n: number) => setPropForm((f: any) => ({ ...f, treatable_sqft: String(n) }))} />}
      <div>
        <label className="label-sm">Lot size (sq ft)</label>
        <input type="number" inputMode="numeric" value={propForm.lot_sqft ?? ''} onChange={e => setPropForm((f: any) => ({ ...f, lot_sqft: e.target.value }))} className="input" placeholder="6000" />
      </div>
      <div>
        <label className="label-sm">Treatable turf (sq ft)</label>
        <input type="number" inputMode="numeric" step="500" value={propForm.treatable_sqft ?? ''} onChange={e => setPropForm((f: any) => ({ ...f, treatable_sqft: e.target.value }))} className="input" placeholder="grass only, to the nearest 500" />
      </div>
      <div className="text-xs text-gray-600 pb-2">
        {sqft > 0 && data?.price
          ? <>Your bands say <b>${data.price}</b>/visit{Number(propForm.mowing_price) !== Number(data.price) && (
              <button type="button" onClick={() => setPropForm((f: any) => ({ ...f, mowing_price: String(data.price) }))} className="ml-2 text-brand-700 font-medium">use it</button>)}</>
          : sqft > 0 && data && !data.has_bands ? 'No price bands set yet (Settings → Automatic quotes).'
          : sqft > 0 && data ? 'No band fits this size — type the price.'
          : 'Set the lot size and the price follows your bands.'}
      </div>
    </div>
  );
}

// ── The satellite's second opinion on a yard ────────────────────────────────
// Owner, Sep 19 2026 (Maps Static turned on): the county map knows the lot and
// the traced roofs; one satellite picture says how much of the green is lawn
// and how much is beds, and sees a house newer than the county's tracing. It
// only ever advises: "Use it" fills the turf box, and saving is still his.
function SatelliteTurf({ prop, onUse }: { prop: any; onUse: (sqft: number) => void }) {
  const readOnly = useReadOnly();   // view-only login: grey out what can only ever be refused
  const qc = useQueryClient();
  const [res, setRes] = useState<any>(null);
  const [tile, setTile] = useState<string | null>(null);
  React.useEffect(() => () => { if (tile) URL.revokeObjectURL(tile); }, [tile]);
  const ask = useMutation(() => api.post(`/properties/${prop.id}/turf-satellite`, {}).then(r => r.data), {
    onSuccess: (d: any) => { setRes(d); qc.invalidateQueries('customers'); },
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Could not look at the yard'); },
  });
  const show = useMutation(() => api.get(`/properties/${prop.id}/turf-tile`, { responseType: 'blob' }).then(r => URL.createObjectURL(r.data as Blob)), {
    onSuccess: (u: string) => setTile(u),
    onError: async (e: any) => { let m = 'Could not get the picture'; try { m = JSON.parse(await e.response.data.text()).message || m; } catch { /* keep the plain words */ } toast.error(m); },
  });
  const note: string | null = res ? res.note : (prop.sat_note || null);
  const sat: number | null = res ? (res.known ? res.sat_turf_sqft : null) : (prop.sat_turf_sqft || null);
  const current = Number(prop.treatable_sqft) || 0;
  return (
    <div className="col-span-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 space-y-1.5">
      <div className="flex items-start gap-2 flex-wrap">
        <p className="flex-1 min-w-[12rem] text-xs text-gray-700">{note || 'No satellite look at this yard yet.'}</p>
        {sat && sat !== current ? <button type="button" onClick={() => onUse(sat)} className="press text-xs px-2.5 py-1.5 rounded-xl bg-brand-600 text-black font-semibold whitespace-nowrap">Use {sat.toLocaleString()}</button> : null}
        <button type="button" onClick={() => (tile ? setTile(null) : show.mutate())} disabled={show.isLoading} className="text-xs px-2.5 py-1.5 rounded-lg border border-gray-300 bg-white text-gray-700 whitespace-nowrap disabled:opacity-50">{show.isLoading ? 'Getting it…' : tile ? 'Hide the picture' : 'Look at the yard'}</button>
        <button type="button" onClick={() => ask.mutate()} disabled={readOnly || (ask.isLoading)} className="text-xs px-2.5 py-1.5 rounded-lg border border-gray-300 bg-white text-gray-700 whitespace-nowrap disabled:opacity-50">{ask.isLoading ? 'Looking…' : note ? 'Look again' : 'Ask the satellite'}</button>
      </div>
      {tile && <img src={tile} alt="The yard from above, lot outlined" className="w-full max-w-md rounded-lg border border-gray-200" />}
      {tile && <p className="text-[11px] text-gray-500">The pink line is the county's lot. Green and straw count as lawn; dark brown as beds. Tree cover reads as lawn, so a shaded yard can come out high.</p>}
    </div>
  );
}
